import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import DriverDashboardHome from '@/components/DriverDashboardHome';
import OrderServiceDocumentsField, {
  openServiceDocumentPreview,
  uploadDriverOrderServiceDocuments,
} from '@/components/OrderServiceDocumentsField';
import { translateAdditionalServiceName } from '@/utils/additionalServiceDisplayName';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  Truck,
  LogOut,
  User,
  FileText,
  Package,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Home,
  Image,
  Edit,
  Search,
  Filter,
  X,
  DollarSign,
  Calendar,
  TrendingUp,
  Receipt,
  Eye,
  Menu,
  Download,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Must match backend `POST /api/drivers/documents/upload` limit */
const DRIVER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
const DRIVER_DOCUMENT_MAX_MB = 10;

async function parseDriverDocumentUploadError(response, t) {
  try {
    const err = await response.json();
    const d = err.detail;
    const text = Array.isArray(d)
      ? d.map((x) => (typeof x === 'object' && x != null && 'msg' in x ? x.msg : String(x))).join('; ')
      : d != null
        ? String(d)
        : '';
    const lower = text.toLowerCase();
    if (lower.includes('file size') || lower.includes('10mb') || lower.includes('10 mb')) {
      return t('driver_error_file_too_large', { maxMB: DRIVER_DOCUMENT_MAX_MB });
    }
    if (text) return text;
  } catch {
    /* ignore parse errors */
  }
  return t('driver_toast_error_upload_document');
}

// Helper function to get emoji for additional services
const getServiceEmoji = (serviceName) => {
  if (!serviceName) return '➕';
  
  const lowerName = serviceName.toLowerCase();
  
  // Limpieza
  if (lowerName.includes('limpieza')) return '🧽✨';
  
  // ITV
  if (lowerName.includes('itv') || lowerName.includes('inspección técnica')) return '🔍✅';
  
  // PIT STOP
  if (lowerName.includes('pit stop') || lowerName.includes('pit-stop') || lowerName.includes('mantenimiento')) return '🛠️';
  
  // Recuperación
  if (lowerName.includes('recuperación') || lowerName.includes('recuperacion')) {
    if (lowerName.includes('europa') || lowerName.includes('continental')) return '🛡️🗺️';
    if (lowerName.includes('isla') || lowerName.includes('islas') || lowerName.includes('canarias') || lowerName.includes('baleares')) return '🛡️🏝️';
    if (lowerName.includes('ceuta') || lowerName.includes('melilla') || lowerName.includes('áfrica') || lowerName.includes('africa')) return '🛡️🌍';
    return '🛡️';
  }
  
  // Representación
  if (lowerName.includes('representación') || lowerName.includes('representacion') || lowerName.includes('verificación') || lowerName.includes('verificacion')) return '🔍📋';
  
  // Default
  return '➕';
};

// Helper function to check if delivery date has passed
const isDeliveryDatePassed = (order) => {
  if (!order) return false;
  
  const now = new Date();
  
  // Try delivery_date_from first
  if (order.delivery_date_from) {
    const deliveryDate = new Date(order.delivery_date_from);
    // If there's a delivery time, use it
    if (order.delivery_time_end) {
      const [hours, minutes] = order.delivery_time_end.split(':').map(Number);
      deliveryDate.setHours(hours, minutes, 0, 0);
    }
    return now > deliveryDate;
  }
  
  // Fallback to service_date
  if (order.service_date) {
    const serviceDate = new Date(order.service_date);
    serviceDate.setHours(23, 59, 59, 999); // End of day
    return now > serviceDate;
  }
  
  return false;
};

/** Hours until pickup; aligns with backend cancel window (48h). */
const getHoursUntilPickupForOrder = (order) => {
  try {
    const dateStr = order.pickup_date_from || order.pickup_date || order.service_date;
    const timeStr = order.pickup_time_start || order.pickup_time;
    if (!dateStr) return null;
    let pickup;
    if (timeStr) {
      pickup = new Date(dateStr);
      const parts = timeStr.split(':').map((p) => parseInt(p, 10));
      const hh = parts[0];
      const mm = parts.length > 1 ? parts[1] : 0;
      if (!Number.isNaN(hh)) pickup.setHours(hh, Number.isNaN(mm) ? 0 : mm, 0, 0);
    } else {
      pickup = new Date(dateStr);
    }
    return (pickup.getTime() - Date.now()) / 3600000;
  } catch {
    return null;
  }
};

const canDriverCancelWithPlatformFee = (order) => {
  const st = order.status;
  if (!st || String(st).includes('Cancelado') || st === 'delivered' || st === 'completed') return false;
  const allowed = ['assigned', 'in_progress', 'confirmed', 'in_transit'];
  if (!allowed.includes(st)) return false;
  const h = getHoursUntilPickupForOrder(order);
  return h !== null && h >= 48;
};

const DRIVER_PLATFORM_CANCEL_FEE_DEFAULT = 50;

// Function to compress image before upload
const compressImage = (file, maxWidth = 800, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions - more aggressive compression
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          // Also limit height
          const maxHeight = 800;
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          console.log(`Compressed image from ${img.width}x${img.height} to ${width}x${height}`);
          resolve(compressedBase64);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// Function to process multiple files with compression
const processFilesWithCompression = async (files) => {
  const compressedPhotos = [];
  for (const file of files) {
    try {
      const compressed = await compressImage(file);
      compressedPhotos.push(compressed);
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fallback to original if compression fails
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      compressedPhotos.push(base64);
    }
  }
  return compressedPhotos;
};

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [driver, setDriver] = useState(null);
  /** Conductor de flota (empleado): no debe ver tarifas ni importes del servicio — debe coincidir con el backend (`_is_fleet_employee`). */
  const isFleetEmployee = Boolean(
    driver?.fleet_driver_id || driver?.fleet_owner_id
  ) && !driver?.is_fleet_driver && !driver?.is_fleet_owner;
  const [documents, setDocuments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [servicesSubTab, setServicesSubTab] = useState('active'); // Sub-tabs: active or historical
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCreateMemberDialog, setShowCreateMemberDialog] = useState(false);
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile menu state
  const [expandedOrderId, setExpandedOrderId] = useState(null); // For compact list expand/collapse
  
  // Service filters
  const [serviceFilters, setServiceFilters] = useState({
    searchText: '',
    dateFrom: '',
    dateTo: '',
    status: 'all', // all, assigned, in_progress, completed
    paymentStatus: 'all', // all, paid, unpaid
    vehicleBrand: '',
    vehicleModel: '',
    vehiclePlate: '',
    fuelType: 'all', // all, gasolina, diesel, electrico, hibrido
    driver: 'all' // all, or specific driver_id
  });
  
  // Billing state
  const [billingPeriods, setBillingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodDetails, setPeriodDetails] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [expandedReceipts, setExpandedReceipts] = useState({}); // Track which expense reports are expanded
  const [currentPeriodData, setCurrentPeriodData] = useState(null); // Current period "EN CURSO"
  const [loadingCurrentPeriod, setLoadingCurrentPeriod] = useState(false);
  const [showCurrentPeriodDetails, setShowCurrentPeriodDetails] = useState(false);
  
  // Fleet management state
  const [fleetMembers, setFleetMembers] = useState([]);
  const [loadingFleet, setLoadingFleet] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedOrderForAssignment, setSelectedOrderForAssignment] = useState(null);
  
  // Incomplete orders state
  const [incompleteOrders, setIncompleteOrders] = useState([]);
  const [loadingIncomplete, setLoadingIncomplete] = useState(false);
  const [showCompleteServiceDialog, setShowCompleteServiceDialog] = useState(false);
  const [selectedOrderToComplete, setSelectedOrderToComplete] = useState(null);
  const [driverPricingTemplates, setDriverPricingTemplates] = useState([]); // For displaying pricing template names
  const [driverInitiatedCancellingId, setDriverInitiatedCancellingId] = useState(null);
  
  // Simplified photo states (multiple photos per section)
  const [pickupPhotos, setPickupPhotos] = useState([]);  // Array of base64 photos
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);  // Array of base64 photos
  const [contractPhotos, setContractPhotos] = useState([]);  // Array of base64 photos
  const [receiptPhotos, setReceiptPhotos] = useState([]);  // Array of base64 photos for expenses
  
  const [uploadingPickupPhotos, setUploadingPickupPhotos] = useState(false);
  const [uploadingDeliveryPhotos, setUploadingDeliveryPhotos] = useState(false);
  const [uploadingContractPhotos, setUploadingContractPhotos] = useState(false);

  /** Optional PDF/images during complete-service dialog (stored as service_documents). */
  const [completionServiceFiles, setCompletionServiceFiles] = useState([]);
  const [uploadingCompletionServiceDocs, setUploadingCompletionServiceDocs] = useState(false);
  
  // Expense report state
  const [expenseForm, setExpenseForm] = useState({
    fuel_expense: 0,
    toll_expense: 0,
    parking_expense: 0,
    taxi_expense: 0,
    other_expense: 0,
    other_expense_description: '',
    receipts: [] // URLs of uploaded receipt photos
  });
  const [uploadingExpenses, setUploadingExpenses] = useState(false);
  
  // Profile editing states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    dni_nie: '',
    fiscal_address: '',
    irpf_retention: 0,
    bank_name: '',
    bank_account: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    file: null,
    upload_date: new Date().toISOString().split('T')[0],
    expiry_date: ''
  });
  const [uploadFileError, setUploadFileError] = useState('');
  const [createMemberForm, setCreateMemberForm] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: '',
    dni_nie: ''
  });
  const [memberDocuments, setMemberDocuments] = useState({
    license_front: null,
    license_back: null,
    dni_front: null,
    dni_back: null
  });

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    const driverData = localStorage.getItem('driver_data');
    
    if (!token || !driverData) {
      navigate('/driver/login');
      return;
    }
    
    setDriver(JSON.parse(driverData));
    loadData();
    
    // Load fleet members if this is a fleet driver
    const driver = JSON.parse(driverData);
    if (driver.is_fleet_driver) {
      loadFleetMembers();
    }
  }, [navigate]);

  // Load fleet members when Mi Flota tab is selected
  useEffect(() => {
    if (activeTab === 'fleet' && driver?.is_fleet_driver) {
      loadFleetMembers();
    }
  }, [activeTab, driver?.is_fleet_driver]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('driver_token');
      const driverData = JSON.parse(localStorage.getItem('driver_data'));
      
      // Load documents
      try {
        const docsResponse = await fetch(`${BACKEND_URL}/api/drivers/${driverData.id}/documents`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (docsResponse.ok) {
          const docsData = await docsResponse.json();
          setDocuments(docsData.documents || []);
        }
      } catch (err) {
        console.error('Error loading documents:', err);
        // Continue loading other data
      }
      
      // Load assigned orders - use driver-specific endpoint
      try {
        const ordersResponse = await fetch(`${BACKEND_URL}/api/drivers/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (ordersResponse.ok) {
          const ordersData = await ordersResponse.json();
          setOrders(ordersData || []);
        }
      } catch (err) {
        console.error('Error loading orders:', err);
        // Orders endpoint may not be available for drivers yet
        setOrders([]);
      }
      
      // Load fleet members if this is a fleet driver
      if (driverData.is_fleet_driver) {
        try {
          const fleetResponse = await fetch(`${BACKEND_URL}/api/drivers/fleet/members`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (fleetResponse.ok) {
            const fleetData = await fleetResponse.json();
            setFleetMembers(fleetData || []);
          }
        } catch (err) {
          console.error('Error loading fleet members:', err);
          setFleetMembers([]);
        }
      }
      
      // Load driver pricing templates
      try {
        const templatesResponse = await fetch(`${BACKEND_URL}/api/driver-pricing-templates`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (templatesResponse.ok) {
          const templatesData = await templatesResponse.json();
          setDriverPricingTemplates(templatesData || []);
        }
      } catch (err) {
        console.error('Error loading pricing templates:', err);
        setDriverPricingTemplates([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      // Only show error if it's a critical failure
      setLoading(false);
    }
  };

  // Function to download contract PDF using form method
  const handleDownloadContract = async (orderId) => {
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/orders/${orderId}/contract`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Contrato_AutoGo_${orderId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('driver_toast_contract_downloaded'));
      } else {
        let msg = t('driver_toast_error_download_contract');
        const text = await response.text();
        try {
          const error = JSON.parse(text);
          if (error.detail) msg = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
        } catch {
          if (text && text.length < 500) msg = text;
        }
        toast.error(msg);
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error(t('driver_toast_error_download_contract'));
    }
  };

  const previewOrderServiceDocument = async (docId) => {
    try {
      const token = localStorage.getItem('driver_token');
      await openServiceDocumentPreview(BACKEND_URL, docId, token);
    } catch (error) {
      console.error('Error opening service document:', error);
      toast.error(t('driver_service_doc_preview_error'));
    }
  };

  const renderDriverOrderNotesAndAttachments = (order) => {
    if (!order) return null;
    const hasNotes = !!(order.notes || order.first_vehicle_notes || order.second_vehicle_notes);
    const docs =
      Array.isArray(order.service_documents) && order.service_documents.length > 0
        ? order.service_documents
        : null;
    if (!hasNotes && !docs) return null;
    return (
      <div className="space-y-3 mb-4">
        {hasNotes && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="font-semibold text-amber-900 mb-2">📝 {t('admin_service_notes')}</div>
            <div className="text-sm space-y-2">
              {order.notes && (
                <div>
                  <span className="text-amber-700 font-medium">{t('admin_notes_general')}</span>
                  <p className="text-amber-900 mt-0.5 whitespace-pre-wrap">{order.notes}</p>
                </div>
              )}
              {order.first_vehicle_notes && (
                <div>
                  <span className="text-amber-700 font-medium">{t('admin_notes_vehicle1')}</span>
                  <p className="text-amber-900 mt-0.5 whitespace-pre-wrap">{order.first_vehicle_notes}</p>
                </div>
              )}
              {order.second_vehicle_notes && (
                <div>
                  <span className="text-amber-700 font-medium">{t('admin_notes_vehicle2')}</span>
                  <p className="text-amber-900 mt-0.5 whitespace-pre-wrap">{order.second_vehicle_notes}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {docs && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 shrink-0" />
              {t('driver_order_attachments_title')}
            </div>
            <ul className="space-y-2">
              {docs.map((doc) => (
                <li
                  key={doc.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm rounded-md bg-white border border-slate-100 px-3 py-2"
                >
                  <span className="truncate min-w-0 flex-1" title={doc.filename}>
                    {doc.filename}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-8"
                    onClick={() => previewOrderServiceDocument(doc.id)}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" />
                    {t('service_docs_preview')}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Fleet management functions
  const loadFleetMembers = async () => {
    if (!driver?.is_fleet_driver) return;
    
    setLoadingFleet(true);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFleetMembers(data);
      }
    } catch (error) {
      console.error('Error loading fleet members:', error);
      toast.error(t('driver_toast_error_load_drivers'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleCreateFleetMember = async (e) => {
    e.preventDefault();
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      
      console.log('Creating fleet member with data:', {
        name: createMemberForm.name,
        email: createMemberForm.email,
        phone: createMemberForm.phone,
        license_number: createMemberForm.license_number,
        dni_nie: createMemberForm.dni_nie
      });
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: createMemberForm.name,
          email: createMemberForm.email,
          phone: createMemberForm.phone,
          license_number: createMemberForm.license_number,
          dni_nie: createMemberForm.dni_nie
        })
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      // Read the response body once and store it
      let data;
      try {
        data = await response.json();
        console.log('Response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error('Respuesta inválida del servidor');
      }
      
      if (response.ok) {
        // Show appropriate message based on whether password is included (email failed)
        if (data.temp_password) {
          toast.success(
            t('driver_toast_driver_created_with_password', { temp_password: data.temp_password }),
            { duration: 10000 }
          );
        } else {
          toast.success(data.message || t('driver_toast_driver_created'));
        }
        setShowCreateMemberDialog(false);
        setCreateMemberForm({
          name: '',
          email: '',
          phone: '',
          license_number: '',
          dni_nie: ''
        });
        await loadFleetMembers();
      } else {
        console.error('Error response:', data);
        // Handle different error types
        const errorMessage = data.detail || data.message || t('driver_error_create_driver_fallback');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Exception creating fleet member:', error);
      toast.error(t('driver_toast_error_create_driver', { message: error.message }));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleUpdateFleetMember = async (memberId, updates) => {
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(t('driver_toast_driver_updated'));
        setShowEditMemberDialog(false);
        setEditingMember(null);
        await loadFleetMembers();
      } else {
        toast.error(data.detail || t('driver_toast_error_update_driver'));
      }
    } catch (error) {
      console.error('Error updating fleet member:', error);
      toast.error(t('driver_toast_error_update_driver'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleDeleteFleetMember = async (memberId, memberName) => {
    if (!window.confirm(t('driver_confirm_delete_member', { name: memberName }))) return;
    
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(t('driver_toast_driver_deleted'));
        await loadFleetMembers();
      } else {
        toast.error(data.detail || t('driver_toast_error_delete_driver'));
      }
    } catch (error) {
      console.error('Error deleting fleet member:', error);
      toast.error(t('driver_toast_error_delete_driver'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleAssignToFleetMember = async (memberId) => {
    if (!selectedOrderForAssignment) return;
    
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/assign-service?order_id=${selectedOrderForAssignment.id}&member_id=${memberId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const isReassignment = selectedOrderForAssignment.driver_id && selectedOrderForAssignment.driver_id !== memberId;
        const isSelf = memberId === driver.id;
        
        if (isSelf) {
          toast.success(t('driver_toast_service_assigned_self'));
        } else if (isReassignment) {
          toast.success(t('driver_toast_service_reassigned'));
        } else {
          toast.success(t('driver_toast_service_assigned'));
        }
        
        setShowAssignDialog(false);
        setSelectedOrderForAssignment(null);
        await loadData(); // Reload orders
      } else {
        toast.error(data.detail || t('driver_toast_error_assign_service'));
      }
    } catch (error) {
      console.error('Error assigning service:', error);
      toast.error(t('driver_toast_error_assign_service'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleDriverInitiatedCancel = async (order) => {
    if (!canDriverCancelWithPlatformFee(order)) {
      toast.error(t('driver_cancel_driver_initiated_error_window'));
      return;
    }
    if (!window.confirm(t('driver_cancel_driver_initiated_confirm', { fee: DRIVER_PLATFORM_CANCEL_FEE_DEFAULT }))) {
      return;
    }
    setDriverInitiatedCancellingId(order.id);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/orders/${order.id}/cancel-driver-initiated`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (!response.ok) {
        const d = data.detail;
        const msg = Array.isArray(d)
          ? d.map((x) => (typeof x === 'object' && x != null && 'msg' in x ? x.msg : String(x))).join('; ')
          : d != null
            ? String(d)
            : '';
        throw new Error(msg || t('driver_cancel_driver_initiated_failed'));
      }
      const feeStr = Number(data.cancellation_fee ?? DRIVER_PLATFORM_CANCEL_FEE_DEFAULT).toFixed(2);
      toast.success(t('driver_cancel_driver_initiated_success', { fee: feeStr }));
      await loadData();
      setExpandedOrderId(null);
    } catch (error) {
      console.error('Driver-initiated cancel:', error);
      toast.error(error.message || t('driver_cancel_driver_initiated_failed'));
    } finally {
      setDriverInitiatedCancellingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_token');


  const loadIncompleteOrders = async () => {
    setLoadingIncomplete(true);
    try {
      const token = localStorage.getItem('driver_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/incomplete-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIncompleteOrders(data || []);
      }
    } catch (error) {
      console.error('Error loading incomplete orders:', error);
    } finally {
      setLoadingIncomplete(false);
    }
  };

    localStorage.removeItem('driver_data');
    toast.success(t('driver_toast_logged_out'));
    navigate('/driver/login');
  };

  // Billing functions
  const loadBillingPeriods = async () => {
    try {
      setLoadingBilling(true);
      const token = localStorage.getItem('driver_token');
      
      // Load liquidation periods from new endpoint
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingPeriods(data.periods || []);
        // Store fleet info if available
        if (data.is_fleet_owner) {
          console.log(`Fleet owner with ${data.fleet_members_count} members`);
        }
      }
    } catch (error) {
      console.error('Error loading billing periods:', error);
      toast.error(t('driver_toast_error_load_billing_periods'));
    } finally {
      setLoadingBilling(false);
    }
  };

  // Load current period data (EN CURSO)
  const loadCurrentPeriod = async () => {
    try {
      setLoadingCurrentPeriod(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations/current-period`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentPeriodData(data);
      }
    } catch (error) {
      console.error('Error loading current period:', error);
    } finally {
      setLoadingCurrentPeriod(false);
    }
  };

  const viewPeriodDetails = async (period) => {
    try {
      setSelectedPeriod(period);
      setLoadingBilling(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/liquidations/${period.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPeriodDetails(data);
        setShowPeriodDialog(true);
      } else {
        toast.error(t('driver_toast_error_load_period_details'));
      }
    } catch (error) {
      console.error('Error loading period details:', error);
      toast.error(t('driver_toast_error_load_period_details'));
    } finally {
      setLoadingBilling(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'invoices') {
      if (billingPeriods.length === 0) {
        loadBillingPeriods();
      }
      if (!currentPeriodData) {
        loadCurrentPeriod();
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'incomplete') {
      loadIncompleteOrders();
    }
  }, [activeTab]);

  const loadIncompleteOrders = async () => {
    try {
      setLoadingIncomplete(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/incomplete-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIncompleteOrders(data || []);
      } else {
        toast.error(t('driver_toast_error_load_incomplete_services'));
      }
    } catch (error) {
      console.error('Error loading incomplete orders:', error);
      toast.error(t('driver_toast_error_load_incomplete_services'));
    } finally {
      setLoadingIncomplete(false);
    }
  };

  const startEditingProfile = () => {
    // Initialize form with current driver data
    setProfileForm({
      full_name: driver?.full_name || '',
      dni_nie: driver?.dni_nie || '',
      fiscal_address: driver?.fiscal_address || '',
      irpf_retention: driver?.irpf_retention || 0,
      bank_name: driver?.bank_name || '',
      bank_account: driver?.bank_account || ''
    });
    setIsEditingProfile(true);
  };

  const saveProfileChanges = async () => {
    try {
      // Validate required fields
      if (!profileForm.full_name || !profileForm.dni_nie || !profileForm.fiscal_address || 
          !profileForm.bank_name || !profileForm.bank_account) {
        toast.error(t('driver_toast_complete_required_fields'));
        return;
      }

      setSavingProfile(true);
      const token = localStorage.getItem('driver_token');
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileForm)
      });

      const data = await response.json();

      if (response.ok) {
        setDriver(data);
        localStorage.setItem('driver_data', JSON.stringify(data));
        toast.success(t('driver_toast_profile_updated'));
        setIsEditingProfile(false);
        
        // Show info about bank account change
        if (profileForm.bank_account !== driver?.bank_account) {
          toast.info(t('driver_toast_new_iban_next_invoice'));
        }
      } else {
        toast.error(data.detail || t('driver_toast_error_update_profile'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('driver_toast_error_save_changes'));
    } finally {
      setSavingProfile(false);
    }
  };

  const pickFleetMemberDocumentFile = (file, inputEl) => {
    if (!file) return null;
    if (file.size > DRIVER_DOCUMENT_MAX_BYTES) {
      toast.error(t('driver_error_file_too_large', { maxMB: DRIVER_DOCUMENT_MAX_MB }));
      if (inputEl) inputEl.value = '';
      return null;
    }
    return file;
  };

  const handleEditMember = (member) => {
    setEditingMember({
      ...member,
      documents: {
        license_front: null,
        license_back: null,
        dni_front: null,
        dni_back: null
      }
    });
    setShowEditMemberDialog(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    
    if (!editingMember) return;
    
    setLoadingFleet(true);
    
    try {
      const token = localStorage.getItem('driver_token');
      
      // Update member info using the correct fleet endpoint
      const response = await fetch(`${BACKEND_URL}/api/drivers/fleet/members/${editingMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingMember.name,
          email: editingMember.email,
          phone: editingMember.phone,
          license_number: editingMember.license_number,
          is_available: editingMember.is_available
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al actualizar miembro');
      }
      
      // Upload new documents if provided
      let uploadedCount = 0;
      const docTypesToUpload = ['license_front', 'license_back', 'dni_front', 'dni_back'];
      
      for (const docType of docTypesToUpload) {
        const f = editingMember.documents?.[docType];
        if (f && f.size > DRIVER_DOCUMENT_MAX_BYTES) {
          toast.error(t('driver_error_file_too_large', { maxMB: DRIVER_DOCUMENT_MAX_MB }));
          return;
        }
      }
      
      for (const docType of docTypesToUpload) {
        if (editingMember.documents && editingMember.documents[docType]) {
          const formData = new FormData();
          formData.append('file', editingMember.documents[docType]);
          formData.append('document_type', docType);
          formData.append('upload_date', new Date().toISOString());
          formData.append('driver_id', editingMember.id);
          
          const docResponse = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (docResponse.ok) {
            uploadedCount++;
          }
        }
      }
      
      toast.success(uploadedCount > 0 ? t('driver_toast_member_updated_with_docs', { count: uploadedCount }) : t('driver_toast_member_updated'));
      setShowEditMemberDialog(false);
      setEditingMember(null);
      await loadFleetMembers(); // Reload fleet members instead of all data
    } catch (error) {
      console.error('Error updating fleet member:', error);
      toast.error(error.message || t('driver_toast_error_update_member'));
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      toast.error(t('driver_toast_select_file'));
      return;
    }
    
    if (uploadForm.file.size > DRIVER_DOCUMENT_MAX_BYTES) {
      const msg = t('driver_error_file_too_large', { maxMB: DRIVER_DOCUMENT_MAX_MB });
      setUploadFileError(msg);
      toast.error(msg);
      return;
    }
    
    setUploadingDoc(true);
    setUploadFileError('');
    
    try {
      const token = localStorage.getItem('driver_token');
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_type', uploadForm.document_type);
      formData.append('upload_date', new Date(uploadForm.upload_date).toISOString());
      if (uploadForm.expiry_date) {
        formData.append('expiry_date', new Date(uploadForm.expiry_date).toISOString());
      }
      
      const response = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const message = await parseDriverDocumentUploadError(response, t);
        setUploadFileError(message);
        toast.error(message);
        return;
      }
      
      toast.success(t('driver_toast_document_uploaded'));
      setShowUploadDialog(false);
      setUploadFileError('');
      setUploadForm({
        document_type: '',
        file: null,
        upload_date: new Date().toISOString().split('T')[0],
        expiry_date: ''
      });
      loadData();
    } catch (error) {
      console.error('Upload error:', error);
      const msg = error?.message && error.message !== 'Failed to fetch'
        ? error.message
        : t('driver_toast_error_upload_document');
      toast.error(msg);
      setUploadFileError(msg);
    } finally {
      setUploadingDoc(false);
    }
  };

  const getDocumentStatus = (doc, t) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', textKey: 'driver_doc_status_pending' },
      approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', textKey: 'driver_doc_status_approved' },
      rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', textKey: 'driver_rejected' },
      expired: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100', textKey: 'driver_doc_status_expired' }
    };
    const config = statusConfig[doc.status] || statusConfig.pending;
    return { ...config, text: t(config.textKey) };
  };

  const getDocumentName = (type, t) => {
    const keys = {
      license_front: 'driver_doc_license_front',
      license_back: 'driver_doc_license_back',
      dni_front: 'driver_doc_dni_front',
      dni_back: 'driver_doc_dni_back',
      autonomo_certificate: 'driver_doc_autonomo',
      hacienda_certificate: 'driver_doc_hacienda',
      ss_certificate: 'driver_doc_ss'
    };
    return keys[type] ? t(keys[type]) : type;
  };

  const requiredDocuments = [
    'license_front',
    'license_back',
    'dni_front',
    'dni_back',
    'autonomo_certificate',
    'hacienda_certificate',
    'ss_certificate'
  ];

  const getDocumentProgress = () => {
    const uploaded = requiredDocuments.filter(docType =>
      documents.some(d => d.document_type === docType)
    ).length;
    return { uploaded, total: requiredDocuments.length };
  };

  // Component to display member documents
  const MemberDocumentsSection = ({ memberId }) => {
    const [memberDocs, setMemberDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchMemberDocuments = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/drivers/${memberId}/documents`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setMemberDocs(data || []);
          }
        } catch (error) {
          console.error('Error loading member documents:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchMemberDocuments();
    }, [memberId]);

    if (loading) {
      return <p className="text-sm text-gray-500">Cargando documentos...</p>;
    }

    const memberRequiredDocs = ['dni_front', 'dni_back', 'license_front', 'license_back'];
    
    return (
      <div className="space-y-2 mt-3">
        {memberRequiredDocs.map((docType) => {
          const doc = memberDocs.find(d => d.document_type === docType);
          const status = doc ? getDocumentStatus(doc, t) : null;
          const StatusIcon = status?.icon;
          
          return (
            <div key={docType} className="flex items-center justify-between bg-white rounded p-2 text-sm">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-gray-700 font-medium">{getDocumentName(docType, t)}</span>
                {doc ? (
                  <div className="flex items-center gap-2">
                    {status && <StatusIcon className={`w-4 h-4 ${status.color}`} />}
                    <span className={`text-xs ${status?.color}`}>{status?.text}</span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">{t('driver_doc_not_uploaded')}</span>
                )}
              </div>
              {doc && doc.file_url && (
                <a 
                  href={`${BACKEND_URL}${doc.file_url}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-xs"
                >
                  {t('driver_view')}
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Filter orders based on service filters
  const filteredOrders = orders.filter(order => {
    // Filter by sub-tab: active or historical
    if (servicesSubTab === 'active') {
      // En Proceso: servicios que NO están completados, entregados o cancelados (excepto Cancelado en Transporte - Pendiente que sí debe aparecer)
      const isCancelled = order.status && (
        order.status.includes('Cancelado') || 
        order.status === 'cancelled'
      );
      const isPendingTransit = order.status === 'Cancelado en Transporte - Pendiente';
      
      if (order.status === 'delivered' || order.status === 'completed') return false;
      if (isCancelled && !isPendingTransit) return false; // Show pending transit in active
    } else if (servicesSubTab === 'historical') {
      // Histórico: servicios completados, entregados O cancelados (excepto Cancelado en Transporte - Pendiente)
      const isCancelled = order.status && (
        order.status.includes('Cancelado') || 
        order.status === 'cancelled'
      );
      const isPendingTransit = order.status === 'Cancelado en Transporte - Pendiente';
      
      if (isPendingTransit) return false; // Don't show pending transit in historical
      if (order.status !== 'delivered' && order.status !== 'completed' && !isCancelled) return false;
    }

    // Text search (direcciones de origen y destino, ciudad)
    if (serviceFilters.searchText) {
      const searchLower = serviceFilters.searchText.toLowerCase();
      const matchesAddress = 
        order.pickup_address?.toLowerCase().includes(searchLower) ||
        order.delivery_address?.toLowerCase().includes(searchLower);
      if (!matchesAddress) return false;
    }

    // Date range filter
    if (serviceFilters.dateFrom) {
      const orderDate = new Date(order.service_date);
      const filterDate = new Date(serviceFilters.dateFrom);
      if (orderDate < filterDate) return false;
    }
    if (serviceFilters.dateTo) {
      const orderDate = new Date(order.service_date);
      const filterDate = new Date(serviceFilters.dateTo);
      if (orderDate > filterDate) return false;
    }

    // Status filter
    if (serviceFilters.status !== 'all') {
      if (order.status !== serviceFilters.status) return false;
    }

    // Payment status filter
    if (serviceFilters.paymentStatus !== 'all') {
      const isPaid = order.payment_status === 'paid' || order.paid === true;
      if (serviceFilters.paymentStatus === 'paid' && !isPaid) return false;
      if (serviceFilters.paymentStatus === 'unpaid' && isPaid) return false;
    }

    // Vehicle brand filter
    if (serviceFilters.vehicleBrand) {
      const brandMatch = order.car_brand?.toLowerCase().includes(serviceFilters.vehicleBrand.toLowerCase());
      if (!brandMatch) return false;
    }

    // Vehicle model filter
    if (serviceFilters.vehicleModel) {
      const modelMatch = order.car_model?.toLowerCase().includes(serviceFilters.vehicleModel.toLowerCase());
      if (!modelMatch) return false;
    }

    // Vehicle plate filter
    if (serviceFilters.vehiclePlate) {
      const plateMatch = order.car_plate?.toLowerCase().includes(serviceFilters.vehiclePlate.toLowerCase());
      if (!plateMatch) return false;
    }

    // Fuel type filter
    if (serviceFilters.fuelType !== 'all') {
      if (order.fuel_type?.toLowerCase() !== serviceFilters.fuelType.toLowerCase()) return false;
    }

    // Driver filter (for fleet owners)
    if (serviceFilters.driver !== 'all') {
      if (order.driver_id !== serviceFilters.driver) return false;
    }

    return true;
  });

  const clearFilters = () => {
    setServiceFilters({
      searchText: '',
      dateFrom: '',
      dateTo: '',
      status: 'all',
      paymentStatus: 'all',
      vehicleBrand: '',
      vehicleModel: '',
      vehiclePlate: '',
      fuelType: 'all',
      driver: 'all'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-[#36e961]" aria-hidden />
        <p className="text-sm text-gray-600">{t('driver_loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-2 sm:py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
                alt="AutoGo Icon" 
                className="h-10 w-10 sm:h-12 sm:w-12"
              />
              <span className="text-2xl sm:text-3xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{driver?.name}</p>
                <p className="text-xs text-gray-600">{driver?.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="hidden sm:flex">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="sm:hidden p-2">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {!driver?.documents_validated && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">{t('driver_docs_pending_title')}</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  {t('driver_docs_pending_text')}
                </p>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* Mobile: Horizontal scrollable tabs */}
          <div className="sm:hidden overflow-x-auto scrollbar-hide -mx-3 px-3">
            <TabsList className="inline-flex w-auto min-w-full space-x-1 bg-transparent p-1">
              <TabsTrigger value="home" className="flex-shrink-0 data-[state=active]:bg-white">
                <Home className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex-shrink-0 data-[state=active]:bg-white">
                <Package className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="incomplete" className="flex-shrink-0 data-[state=active]:bg-white">
                <AlertTriangle className="w-4 h-4" />
              </TabsTrigger>
              {driver?.is_fleet_driver && (
                <TabsTrigger value="fleet" className="flex-shrink-0 data-[state=active]:bg-white">
                  <Truck className="w-4 h-4" />
                </TabsTrigger>
              )}
              <TabsTrigger value="profile" className="flex-shrink-0 data-[state=active]:bg-white">
                <User className="w-4 h-4" />
              </TabsTrigger>
              {!driver?.fleet_driver_id && (
                <TabsTrigger value="invoices" className="flex-shrink-0 data-[state=active]:bg-white">
                  <DollarSign className="w-4 h-4" />
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          
          {/* Desktop: Grid tabs with labels */}
          <TabsList className={`hidden sm:grid w-full ${driver?.is_fleet_driver ? 'sm:grid-cols-6' : 'sm:grid-cols-5'} max-w-4xl`}>
            <TabsTrigger value="home" className="text-sm">
              <Home className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_home')}</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="text-sm">
              <Package className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_services')}</span>
            </TabsTrigger>
            <TabsTrigger value="incomplete" className="text-sm">
              <AlertTriangle className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_complete')}</span>
            </TabsTrigger>
            {driver?.is_fleet_driver && (
              <TabsTrigger value="fleet" className="text-sm">
                <Truck className="w-4 h-4 mr-2" />
                <span>{t('driver_nav_fleet')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="text-sm">
              <User className="w-4 h-4 mr-2" />
              <span>{t('driver_nav_profile')}</span>
            </TabsTrigger>
            {!driver?.fleet_driver_id && (
              <TabsTrigger value="invoices" className="text-sm">
                <DollarSign className="w-4 h-4 mr-2" />
                <span>{t('driver_nav_billing')}</span>
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="home">
            <DriverDashboardHome 
              driver={driver}
              orders={orders}
              documents={documents}
              onTabChange={setActiveTab}
              hideOrderPrices={isFleetEmployee}
            />
          </TabsContent>

          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>{t('driver_services_title')}</CardTitle>
                <CardDescription>{t('driver_services_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs for Active / Historical */}
                <div className="flex gap-2 mb-6 border-b">
                  <button
                    onClick={() => setServicesSubTab('active')}
                    className={`px-4 py-2 font-medium transition-colors relative ${
                      servicesSubTab === 'active'
                        ? 'text-[#36e961] border-b-2 border-[#36e961]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('driver_services_in_progress')}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                      {orders.filter(o => {
                        const isCancelled = o.status && (o.status.includes('Cancelado') || o.status === 'cancelled');
                        return o.status !== 'delivered' && o.status !== 'completed' && !isCancelled;
                      }).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setServicesSubTab('historical')}
                    className={`px-4 py-2 font-medium transition-colors relative ${
                      servicesSubTab === 'historical'
                        ? 'text-[#36e961] border-b-2 border-[#36e961]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('driver_services_historical')}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                      {orders.filter(o => {
                        const isCancelled = o.status && (o.status.includes('Cancelado') || o.status === 'cancelled');
                        return o.status === 'delivered' || o.status === 'completed' || isCancelled;
                      }).length}
                    </span>
                  </button>
                </div>

                {/* Filters Section */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-600" />
                      <h3 className="font-semibold text-sm">{t('driver_filters_title')}</h3>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearFilters}
                      className="h-7 text-xs"
                    >
                      <X className="w-3 h-3 mr-1" />
                      {t('driver_clear')}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Search by address/city */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_search_address')}</Label>
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder={t('ph_cities_search')}
                          value={serviceFilters.searchText}
                          onChange={(e) => setServiceFilters({...serviceFilters, searchText: e.target.value})}
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* Date From */}
                    <div>
                      <Label className="text-xs mb-1">Fecha Desde</Label>
                      <Input
                        type="date"
                        value={serviceFilters.dateFrom}
                        onChange={(e) => setServiceFilters({...serviceFilters, dateFrom: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Date To */}
                    <div>
                      <Label className="text-xs mb-1">{t('date_to')}</Label>
                      <Input
                        type="date"
                        value={serviceFilters.dateTo}
                        onChange={(e) => setServiceFilters({...serviceFilters, dateTo: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_status')}</Label>
                      <Select 
                        value={serviceFilters.status} 
                        onValueChange={(value) => setServiceFilters({...serviceFilters, status: value})}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('driver_all')}</SelectItem>
                          <SelectItem value="assigned">{t('service_status_assigned')}</SelectItem>
                          <SelectItem value="in_progress">{t('admin_status_in_progress')}</SelectItem>
                          <SelectItem value="completed">{t('calendar_legend_completed')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>


                    {/* Driver filter (only for fleet owners) */}
                    {driver.is_fleet_driver && (
                      <div>
                        <Label className="text-xs mb-1">{t('driver_driver_label')}</Label>
                        <Select 
                          value={serviceFilters.driver} 
                          onValueChange={(value) => setServiceFilters({...serviceFilters, driver: value})}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('driver_all')}</SelectItem>
                            <SelectItem value={driver.id}>{t('driver_myself')}</SelectItem>
                            {fleetMembers.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}


                    {/* Vehicle Brand */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_vehicle_brand')}</Label>
                      <Input
                        placeholder={t('ph_brands')}
                        value={serviceFilters.vehicleBrand}
                        onChange={(e) => setServiceFilters({...serviceFilters, vehicleBrand: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Vehicle Model */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_vehicle_model')}</Label>
                      <Input
                        placeholder={t('ph_models')}
                        value={serviceFilters.vehicleModel}
                        onChange={(e) => setServiceFilters({...serviceFilters, vehicleModel: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Vehicle Plate */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_plate')}</Label>
                      <Input
                        placeholder={t('ph_plate_example')}
                        value={serviceFilters.vehiclePlate}
                        onChange={(e) => setServiceFilters({...serviceFilters, vehiclePlate: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Fuel Type */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_fuel_type')}</Label>
                      <Select 
                        value={serviceFilters.fuelType} 
                        onValueChange={(value) => setServiceFilters({...serviceFilters, fuelType: value})}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('driver_all')}</SelectItem>
                          <SelectItem value="gasolina">{t('driver_fuel_gasoline')}</SelectItem>
                          <SelectItem value="diesel">{t('driver_fuel_diesel')}</SelectItem>
                          <SelectItem value="electrico">{t('driver_fuel_electric')}</SelectItem>
                          <SelectItem value="hibrido">{t('driver_fuel_hybrid')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Payment Status */}
                    <div>
                      <Label className="text-xs mb-1">{t('driver_payment_status')}</Label>
                      <Select 
                        value={serviceFilters.paymentStatus} 
                        onValueChange={(value) => setServiceFilters({...serviceFilters, paymentStatus: value})}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('driver_all')}</SelectItem>
                          <SelectItem value="paid">{t('driver_paid')}</SelectItem>
                          <SelectItem value="unpaid">{t('driver_unpaid')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Results count */}
                  <div className="mt-3 text-xs text-gray-600">
                    {t('driver_showing_services', { shown: filteredOrders.length, total: orders.length })}
                  </div>
                </div>

                {filteredOrders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-semibold mb-1">
                      {orders.length === 0 ? t('driver_no_orders') : t('driver_no_orders_filter')}
                    </p>
                    {orders.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={clearFilters}
                        className="mt-2"
                      >
                        {t('driver_clear_filters')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    {/* Compact Table Header */}
                    <div className="bg-gray-100 border-b grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-600 uppercase">
                      <div className="col-span-1">{t('driver_table_date')}</div>
                      <div className="col-span-3">{t('driver_table_trip')}</div>
                      <div className="col-span-2">{t('driver_table_vehicle')}</div>
                      <div className="col-span-2">{t('driver_table_price')}</div>
                      <div className="col-span-2">{t('driver_table_status')}</div>
                      <div className="col-span-2 text-right">{t('driver_table_actions')}</div>
                    </div>
                    
                    {/* Compact Table Rows */}
                    <div className="divide-y">
                      {filteredOrders.map((order) => {
                        const isOverdue = isDeliveryDatePassed(order) && (order.status === 'assigned' || order.status === 'in_progress');
                        const isExpanded = expandedOrderId === order.id;
                        
                        return (
                          <div key={order.id} className={`${isOverdue ? 'bg-red-50' : 'bg-white'}`}>
                            {/* Compact Row */}
                            <div 
                              className={`grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm cursor-pointer hover:bg-gray-50 transition-colors ${isOverdue ? 'border-l-4 border-red-500' : ''}`}
                              onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                            >
                              {/* Fecha */}
                              <div className="col-span-1 text-xs">
                                <div className="font-medium">
                                  {order.pickup_date_from 
                                    ? new Date(order.pickup_date_from).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { day: '2-digit', month: 'short' })
                                    : (order.service_date ? new Date(order.service_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { day: '2-digit', month: 'short' }) : 'N/A')
                                  }
                                </div>
                                {order.pickup_time_start && (
                                  <div className="text-gray-500">{order.pickup_time_start}</div>
                                )}
                              </div>
                              
                              {/* Trayecto */}
                              <div className="col-span-3">
                                <div className="flex items-center gap-1 text-xs">
                                  <span className="text-green-600">●</span>
                                  <span className="truncate max-w-[100px]" title={order.pickup_address}>
                                    {order.pickup_address?.split(',')[0] || t('driver_origin_short')}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-red-600">●</span>
                                  <span className="truncate max-w-[100px]" title={order.delivery_address}>
                                    {order.delivery_address?.split(',')[0] || t('driver_destination_short')}
                                  </span>
                                </div>
                                {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes || order.service_documents?.length > 0) && (
                                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-600">
                                    {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes) && (
                                      <span title={t('admin_service_notes')}>📝</span>
                                    )}
                                    {order.service_documents?.length > 0 && (
                                      <span title={t('driver_order_attachments_title')}>
                                        📎{order.service_documents.length}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Vehículo */}
                              <div className="col-span-2 text-xs">
                                <div className="font-mono font-semibold">{order.car_plate}</div>
                                <div className="text-gray-500 truncate">{order.car_brand} {order.car_model}</div>
                              </div>
                              
                              {/* Precio */}
                              <div className="col-span-2">
                                <div className="font-bold text-[#36e961]">
                                  {(isFleetEmployee || order._prices_hidden) ? '—' : `€${order.driver_price?.toFixed(2) || '0.00'}`}
                                </div>
                                <div className="text-xs text-gray-500">{order.distance_km ? `${order.distance_km.toFixed(0)} km` : ''}</div>
                              </div>
                              
                              {/* Estado */}
                              <div className="col-span-2">
                                <div className="flex items-center gap-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    order.status === 'completed' || order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                    order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                    order.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                                    order.status?.includes('Cancelado') ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {order.status === 'completed' || order.status === 'delivered' ? t('calendar_legend_completed') :
                                     order.status === 'in_progress' ? t('admin_status_in_progress') :
                                     order.status === 'assigned' ? t('service_status_assigned') : 
                                     order.status?.includes('Cancelado') ? t('service_status_cancelled') : order.status}
                                  </span>
                                  {isOverdue && (
                                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-red-600 text-white animate-pulse">
                                      !
                                    </span>
                                  )}
                                </div>
                                {driver?.is_fleet_driver && order.driver_name && order.driver_id !== driver.id && (
                                  <div className="text-xs text-blue-600 truncate">{order.driver_name}</div>
                                )}
                              </div>
                              
                              {/* Acciones */}
                              <div className="col-span-2 flex items-center justify-end gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedOrderId(isExpanded ? null : order.id);
                                  }}
                                >
                                  {isExpanded ? t('admin_hide') : t('driver_details')}
                                </Button>
                              </div>
                            </div>
                            
                            {/* Expanded Details */}
                            {isExpanded && (
                              <div className={`px-4 py-4 border-t ${isOverdue ? 'bg-red-50' : 'bg-gray-50'}`}>
                                {/* Urgent Banner */}
                                {isOverdue && (
                                  <div className="bg-red-600 text-white px-3 py-2 rounded-lg mb-4 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-sm font-medium">⚠️ {t('driver_overdue_banner')}</span>
                                  </div>
                                )}
                                
                                {/* Service Info Header */}
                                <div className="bg-[#1a1f2e] text-white rounded-lg p-4 mb-4">
                                  <div className="flex justify-between items-center mb-3">
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg font-bold">#{order.id.substring(0, 8).toUpperCase()}</span>
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#36e961] text-white">
                                        {order.service_type || t('driver_service_type_delivery')}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        order.status === 'completed' || order.status === 'delivered' ? 'bg-green-500' :
                                        order.status === 'in_progress' ? 'bg-blue-500' :
                                        order.status === 'assigned' ? 'bg-yellow-500' :
                                        'bg-gray-500'
                                      }`}>
                                        {order.status === 'completed' || order.status === 'delivered' ? t('calendar_legend_completed').toUpperCase() :
                                         order.status === 'in_progress' ? t('admin_status_in_progress').toUpperCase() :
                                         order.status === 'assigned' ? t('service_status_assigned').toUpperCase() : order.status?.toUpperCase()}
                                      </span>
                                    </div>
                                    {order.client_name && (
                                      <div className="text-sm text-gray-300">{order.client_name}</div>
                                    )}
                                  </div>
                                  
                                  {/* Stats Row */}
                                  <div className="grid grid-cols-4 gap-4 text-center">
                                    <div>
                                      <div className="text-2xl font-bold text-[#36e961]">
                                        {(isFleetEmployee || order._prices_hidden) ? '—' : `€${order.driver_price?.toFixed(2) || '0.00'}`}
                                      </div>
                                      <div className="text-xs text-gray-400">{t('driver_label_remuneration')}</div>
                                    </div>
                                    <div>
                                      <div className="text-lg font-semibold">{order.distance_km ? `${order.distance_km.toFixed(0)} km` : 'N/A'}</div>
                                      <div className="text-xs text-gray-400">{t('driver_label_distance')}</div>
                                    </div>
                                    <div>
                                      <div className="text-lg font-semibold">{order.car_plate || 'N/A'}</div>
                                      <div className="text-xs text-gray-400">{t('driver_plate')}</div>
                                    </div>
                                    <div>
                                      <div className="text-lg font-semibold">{order.car_brand} {order.car_model}</div>
                                      <div className="text-xs text-gray-400">{t('driver_label_vehicle')}</div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Pickup and Delivery Cards */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  {/* Pickup */}
                                  <div className="bg-white border-l-4 border-green-500 rounded-lg p-4 shadow-sm">
                                    <div className="font-bold text-green-700 mb-2 flex items-center gap-2">
                                      <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                                      {t('driver_pickup')}
                                    </div>
                                    <div className="text-sm font-medium mb-2">{order.pickup_address}</div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div>📅 {order.pickup_date_from ? new Date(order.pickup_date_from).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : (order.service_date ? new Date(order.service_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB') : t('driver_date_unspecified'))}</div>
                                      {(order.pickup_time_start || order.pickup_time_end) && (
                                        <div>🕐 {order.pickup_time_start || ''} {order.pickup_time_end ? `- ${order.pickup_time_end}` : ''}</div>
                                      )}
                                      {order.pickup_contact_name && (
                                        <div className="pt-2 border-t mt-2">
                                          👤 {order.pickup_contact_name}
                                          {order.pickup_contact_phone && (
                                            <a href={`tel:${order.pickup_contact_phone}`} className="text-blue-600 ml-2">📞 {order.pickup_contact_phone}</a>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                      <a 
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.pickup_address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-white hover:bg-gray-50 border rounded px-2 py-1.5 flex items-center justify-center gap-1 text-xs transition-colors"
                                      >
                                        <img src="https://customer-assets.emergentagent.com/job_fleetpoint-2/artifacts/56ws4pal_image.png" alt="Maps" className="w-4 h-4" />
                                        Maps
                                      </a>
                                      <a 
                                        href={`https://waze.com/ul?q=${encodeURIComponent(order.pickup_address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-white hover:bg-gray-50 border rounded px-2 py-1.5 flex items-center justify-center gap-1 text-xs transition-colors"
                                      >
                                        <img src="https://customer-assets.emergentagent.com/job_fleetpoint-2/artifacts/p8fe9kk5_image.png" alt="Waze" className="w-4 h-4" />
                                        Waze
                                      </a>
                                    </div>
                                  </div>
                                  
                                  {/* Delivery */}
                                  <div className="bg-white border-l-4 border-red-500 rounded-lg p-4 shadow-sm">
                                    <div className="font-bold text-red-700 mb-2 flex items-center gap-2">
                                      <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                                      {t('driver_delivery')}
                                    </div>
                                    <div className="text-sm font-medium mb-2">{order.delivery_address}</div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div>📅 {order.delivery_date_from ? new Date(order.delivery_date_from).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : (order.delivery_margin_time && order.delivery_margin_time.includes('|') ? order.delivery_margin_time.split('|')[0].trim() : t('no_delivery_date'))}</div>
                                      {(order.delivery_time_start || order.delivery_time_end) && (
                                        <div>🕐 {order.delivery_time_start || ''} {order.delivery_time_end ? `- ${order.delivery_time_end}` : ''}</div>
                                      )}
                                      {order.delivery_contact_name && (
                                        <div className="pt-2 border-t mt-2">
                                          👤 {order.delivery_contact_name}
                                          {order.delivery_contact_phone && (
                                            <a href={`tel:${order.delivery_contact_phone}`} className="text-blue-600 ml-2">📞 {order.delivery_contact_phone}</a>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                      <a 
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-white hover:bg-gray-50 border rounded px-2 py-1.5 flex items-center justify-center gap-1 text-xs transition-colors"
                                      >
                                        <img src="https://customer-assets.emergentagent.com/job_fleetpoint-2/artifacts/56ws4pal_image.png" alt="Maps" className="w-4 h-4" />
                                        Maps
                                      </a>
                                      <a 
                                        href={`https://waze.com/ul?q=${encodeURIComponent(order.delivery_address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-white hover:bg-gray-50 border rounded px-2 py-1.5 flex items-center justify-center gap-1 text-xs transition-colors"
                                      >
                                        <img src="https://customer-assets.emergentagent.com/job_fleetpoint-2/artifacts/p8fe9kk5_image.png" alt="Waze" className="w-4 h-4" />
                                        Waze
                                      </a>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Vehicle Info */}
                                <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                                  <div className="font-bold text-gray-700 mb-3">🚗 Vehículo</div>
                                  <div className="grid grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <div className="text-xs text-gray-500">Marca</div>
                                      <div className="font-medium">{order.car_brand || 'N/A'}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-gray-500">Modelo</div>
                                      <div className="font-medium">{order.car_model || 'N/A'}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-gray-500">Matrícula</div>
                                      <div className="font-mono font-bold">{order.car_plate || 'N/A'}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-gray-500">Combustible</div>
                                      <div className="font-medium">{order.fuel_type || 'N/A'}</div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Additional Services */}
                                {order.additional_services && order.additional_services.length > 0 && (
                                  <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                                    <div className="font-bold text-gray-700 mb-3">➕ {t('additional_services')}</div>
                                    <div className="flex flex-wrap gap-2">
                                      {order.additional_services.map((service, index) => (
                                        <span key={index} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 rounded-full border border-blue-200 text-sm">
                                          <span>{getServiceEmoji(service.name)}</span>
                                          <span className="font-medium">{translateAdditionalServiceName(service.name, t)}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {renderDriverOrderNotesAndAttachments(order)}

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-2 pt-4 border-t">
                                  {/* Download Contract */}
                                  <Button
                                    onClick={() => handleDownloadContract(order.id)}
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2"
                                  >
                                    <FileText className="w-4 h-4" />
                                    {t('driver_download_contract')}
                                  </Button>
                                  
                                  {/* Assign to Fleet Member - Only for fleet drivers */}
                                  {driver?.is_fleet_driver && (order.driver_id === driver.id || order.fleet_driver_id === driver.id) && (
                                    <Button
                                      onClick={() => {
                                        setSelectedOrderForAssignment(order);
                                        setShowAssignDialog(true);
                                      }}
                                      variant="outline"
                                      size="sm"
                                      className="flex items-center gap-2"
                                    >
                                      <User className="w-4 h-4" />
                                      {order.driver_id === driver.id ? t('driver_assign_driver') : t('driver_reassign_driver')}
                                    </Button>
                                  )}
                                  
                                  {/* Complete Service Button */}
                                  {(order.status === 'assigned' || order.status === 'in_progress') && (
                                    <Button
                                      onClick={() => {
                                        setCompletionServiceFiles([]);
                                        setSelectedOrderToComplete(order);
                                        setShowCompleteServiceDialog(true);
                                      }}
                                      size="sm"
                                      className={`flex items-center gap-2 ${
                                        isOverdue 
                                          ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                                          : 'bg-[#36e961] hover:bg-[#2dd351]'
                                      }`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      {t('driver_complete_service')}
                                      {isOverdue && (
                                        <span className="ml-1 px-1.5 py-0.5 bg-white text-red-600 rounded text-xs font-bold">
                                          {t('driver_urgent')}
                                        </span>
                                      )}
                                    </Button>
                                  )}
                                  {canDriverCancelWithPlatformFee(order) && (
                                    <div className="w-full mt-2 space-y-1">
                                      <p className="text-xs text-amber-800 max-w-xl">
                                        {t('driver_cancel_driver_initiated_help', { fee: DRIVER_PLATFORM_CANCEL_FEE_DEFAULT })}
                                      </p>
                                      <Button
                                        onClick={() => handleDriverInitiatedCancel(order)}
                                        variant="outline"
                                        size="sm"
                                        disabled={driverInitiatedCancellingId === order.id}
                                        className="flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-50"
                                      >
                                        {driverInitiatedCancellingId === order.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <XCircle className="w-4 h-4" />
                                        )}
                                        {t('driver_cancel_driver_initiated')}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="incomplete">
            <Card>
              <CardHeader>
                <CardTitle>{t('driver_incomplete_title')}</CardTitle>
                <CardDescription>
                  {t('driver_incomplete_subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingIncomplete ? (
                  <div className="text-center py-8">{t('driver_loading_services')}</div>
                ) : incompleteOrders.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <p className="text-lg font-semibold">{t('driver_all_services_completed')}</p>
                    <p className="text-sm text-gray-600">{t('driver_no_pending_complete')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {incompleteOrders.map((order) => (
                      <Card key={order.id} className="border-orange-200 bg-orange-50">
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Package className="w-5 h-5 text-orange-600" />
                                <span className="font-semibold text-lg">{order.car_brand} {order.car_model}</span>
                                <span className="text-sm bg-orange-600 text-white px-2 py-1 rounded">
                                  {order.car_plate}
                                </span>
                                {/* Mostrar conductor si es diferente del usuario actual (para fleet owners) */}
                                {driver?.is_fleet_driver && order.driver_id !== driver.id && order.driver_name && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {order.driver_name}
                                  </span>
                                )}
                                {order.approval_status === 'pending_approval' && (
                                  <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {t('driver_pending_approval')}
                                  </span>
                                )}
                                {order.approval_status === 'rejected' && (
                                  <span className="text-xs bg-red-500 text-white px-2 py-1 rounded flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    {t('driver_rejected')}
                                  </span>
                                )}
                              </div>
                              
                              {/* Detalles del servicio */}
                              <div className="bg-white border border-orange-300 rounded-lg p-3 mb-3">
                                <div className="grid grid-cols-3 gap-3 text-xs">
                                  <div>
                                    <div className="text-orange-700 font-medium mb-1">{t('driver_service_id')}</div>
                                    <div className="text-gray-900 font-mono text-lg font-bold">{order.id.substring(0, 8).toUpperCase()}</div>
                                  </div>
                                  <div>
                                    <div className="text-orange-700 font-medium mb-1">📅 {t('driver_date_pickup')}</div>
                                    <div className="text-gray-900">
                                      {order.pickup_date_from ? new Date(order.pickup_date_from).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      }) : (order.service_date ? new Date(order.service_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      }) : t('driver_date_unspecified'))}
                                    </div>
                                    {(order.pickup_time_start || order.pickup_time_end) && (
                                      <div className="text-gray-600 text-sm mt-1">
                                        🕐 {order.pickup_time_start && order.pickup_time_end 
                                          ? `${order.pickup_time_start} - ${order.pickup_time_end}`
                                          : order.pickup_time_start || order.pickup_time_end}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-orange-700 font-medium mb-1">📅 {t('driver_date_delivery_label')}</div>
                                    <div className="text-gray-900">
                                      {order.delivery_date_from ? new Date(order.delivery_date_from).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      }) : (order.delivery_margin_time && order.delivery_margin_time.includes('|') ? order.delivery_margin_time.split('|')[0].trim() : t('no_delivery_date'))}
                                    </div>
                                    {(order.delivery_time_start || order.delivery_time_end || (order.delivery_margin_time && order.delivery_margin_time.includes('|'))) && (
                                      <div className="text-gray-600 text-sm mt-1">
                                        🕐 {order.delivery_time_start && order.delivery_time_end
                                          ? `${order.delivery_time_start} - ${order.delivery_time_end}`
                                          : (order.delivery_time_start || order.delivery_time_end || (order.delivery_margin_time && order.delivery_margin_time.includes('|') ? order.delivery_margin_time.split('|')[1]?.trim() : null))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {order.approval_status === 'pending_approval' && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                                  <p className="text-sm text-yellow-800">
                                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                                    {t('driver_pending_approval_message')}
                                  </p>
                                </div>
                              )}
                              
                              {order.approval_status === 'rejected' && order.rejection_reason && (
                                <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                                  <p className="text-sm font-medium text-red-800">{t('driver_rejection_reason')}</p>
                                  <p className="text-sm text-red-700">{order.rejection_reason}</p>
                                  <p className="text-xs text-red-600 mt-2">{t('driver_reupload_photos_expenses')}</p>
                                </div>
                              )}
                              
                              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                                <div>
                                  <p className="text-gray-600">{t('calendar_origin')}</p>
                                  <p className="font-medium">{order.pickup_address}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">{t('calendar_destination')}</p>
                                  <p className="font-medium">{order.delivery_address}</p>
                                </div>
                              </div>

                              {renderDriverOrderNotesAndAttachments(order)}

                              <div className="mt-4 flex gap-4">
                                {!order.has_pickup_photos && (
                                  <div className="flex items-center gap-2 text-red-600">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('driver_missing_pickup_photos')}</span>
                                  </div>
                                )}
                                {order.has_pickup_photos && (
                                  <div className="flex items-center gap-2 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('driver_pickup_photos_uploaded')}</span>
                                  </div>
                                )}
                                
                                {!order.has_delivery_photos && (
                                  <div className="flex items-center gap-2 text-red-600">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('driver_missing_delivery_photos')}</span>
                                  </div>
                                )}
                                {order.has_delivery_photos && (
                                  <div className="flex items-center gap-2 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('driver_delivery_photos_uploaded')}</span>
                                  </div>
                                )}
                                
                                {!order.has_expense_report && (
                                  <div className="flex items-center gap-2 text-red-600">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('driver_missing_expense_report')}</span>
                                  </div>
                                )}
                                {order.has_expense_report && (
                                  <div className="flex items-center gap-2 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('driver_expense_reported')}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <Button 
                              onClick={() => {
                                setCompletionServiceFiles([]);
                                setSelectedOrderToComplete(order);
                                setShowCompleteServiceDialog(true);
                              }}
                              className="bg-orange-600 hover:bg-orange-700"
                              disabled={order.approval_status === 'pending_approval'}
                            >
                              {order.approval_status === 'pending_approval' ? t('driver_pending_approval') : t('driver_complete_service')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

            <TabsContent value="fleet">
              <div className="space-y-6">
                {/* Header with Create Button */}
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">{t('driver_nav_fleet')}</h2>
                    <p className="text-gray-600 mt-1">{t('driver_fleet_manage_subtitle')}</p>
                  </div>
                  <Button onClick={() => setShowCreateMemberDialog(true)}>
                    <User className="w-4 h-4 mr-2" />
                    {t('add_driver')}
                  </Button>
                </div>

                {/* Fleet Members List */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('driver_fleet_members_title')}</CardTitle>
                    <CardDescription>
                      {fleetMembers.length === 1 ? t('driver_fleet_members_count', { count: fleetMembers.length }) : t('driver_fleet_members_count_plural', { count: fleetMembers.length })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {fleetMembers.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <User className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-lg font-medium">{t('driver_fleet_no_members')}</p>
                        <p className="text-sm mt-1">{t('driver_fleet_add_to_start')}</p>
                        <Button className="mt-4" onClick={() => setShowCreateMemberDialog(true)}>
                          {t('driver_fleet_add_first')}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {fleetMembers.map((member) => (
                          <div key={member.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-gray-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-lg">{member.name}</h3>
                                    <p className="text-sm text-gray-600">{member.email}</p>
                                  </div>
                                </div>
                                
                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_phone_short')}</span>
                                    <p className="font-medium">{member.phone}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_license_no')}</span>
                                    <p className="font-medium">{member.license_number}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_status')}</span>
                                    <p className="font-medium">
                                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                        member.status === 'available' ? 'bg-green-100 text-green-800' :
                                        member.status === 'busy' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {member.status === 'available' ? t('driver_status_available') :
                                         member.status === 'busy' ? t('driver_status_busy') : t('driver_status_disconnected')}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">{t('driver_label_documents')}</span>
                                    <p className="font-medium">
                                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                        member.documents_validated ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {member.documents_validated ? (
                                          <>
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            {t('driver_validated')}
                                          </>
                                        ) : (
                                          <>
                                            <Clock className="w-3 h-3 mr-1" />
                                            {t('driver_doc_pending')}
                                          </>
                                        )}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditMember(member)}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  {t('edit')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>{t('driver_profile_title')}</CardTitle>
                <CardDescription>{t('driver_profile_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('driver_label_name')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.name}</p>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <p className="mt-1 text-gray-900">{driver?.email}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_phone')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.phone}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_dni')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.license_number}</p>
                  </div>
                  <div>
                    <Label>{t('driver_table_vehicle')}</Label>
                    <p className="mt-1 text-gray-900">{driver?.vehicle_info || t('driver_not_specified')}</p>
                  </div>
                  <div>
                    <Label>{t('driver_label_status')}</Label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        driver?.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {driver?.is_available ? t('driver_status_available') : t('driver_status_not_available')}
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Label>{t('driver_doc_validation')}</Label>
                  <div className="mt-2 flex items-center gap-2">
                    {driver?.documents_validated ? (
                      <span className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{t('admin_documents_validated')}</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-yellow-600">
                        <Clock className="w-5 h-5" />
                        <span className="font-medium">{t('driver_validation_pending')}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Billing Information Section */}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <Label className="text-lg font-semibold">{t('driver_billing_title')}</Label>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('driver_billing_description')}
                      </p>
                    </div>
                    {!isEditingProfile ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={startEditingProfile}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        {t('edit')}
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setIsEditingProfile(false)}
                          disabled={savingProfile}
                        >
                          {t('cancel')}
                        </Button>
                        <Button 
                          size="sm"
                          onClick={saveProfileChanges}
                          disabled={savingProfile}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {savingProfile ? t('driver_saving') : t('save_changes')}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>{t('driver_billing_full_name')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.full_name}
                          onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                          placeholder={t('ph_fullname_example')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.full_name || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_dni')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.dni_nie}
                          onChange={(e) => setProfileForm({...profileForm, dni_nie: e.target.value})}
                          placeholder={t('ph_dni_example')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.dni_nie || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_irpf')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={profileForm.irpf_retention}
                          onChange={(e) => setProfileForm({...profileForm, irpf_retention: parseFloat(e.target.value) || 0})}
                          placeholder={t('ph_number_short')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.irpf_retention || 0}%</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Label>{t('driver_billing_fiscal_address')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.fiscal_address}
                          onChange={(e) => setProfileForm({...profileForm, fiscal_address: e.target.value})}
                          placeholder={t('ph_fiscal_address')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.fiscal_address || t('driver_date_unspecified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_bank')} *</Label>
                      {isEditingProfile ? (
                        <Input
                          value={profileForm.bank_name}
                          onChange={(e) => setProfileForm({...profileForm, bank_name: e.target.value})}
                          placeholder={t('ph_city_example')}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.bank_name || t('driver_not_specified')}</p>
                      )}
                    </div>
                    <div>
                      <Label>{t('driver_billing_iban')} *</Label>
                      {isEditingProfile ? (
                        <div>
                          <Input
                            value={profileForm.bank_account}
                            onChange={(e) => setProfileForm({...profileForm, bank_account: e.target.value})}
                            placeholder={t('ph_iban_example')}
                            className="mt-1"
                          />
                          <p className="text-xs text-blue-600 mt-1">
                            {t('driver_billing_iban_note')}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-gray-900">{driver?.bank_account || t('driver_not_specified')}</p>
                      )}
                    </div>
                  </div>
                  
                  {!isEditingProfile && (!driver?.full_name || !driver?.dni_nie || !driver?.fiscal_address || !driver?.bank_name || !driver?.bank_account) && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        {t('driver_billing_incomplete_warning')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <Label className="text-lg font-semibold">{t('driver_my_documents')}</Label>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('driver_docs_progress', { uploaded: getDocumentProgress().uploaded, total: getDocumentProgress().total })}
                      </p>
                    </div>
                    <Button onClick={() => setShowUploadDialog(true)} className="bg-[#36e961] hover:bg-[#2dd156]" size="sm">
                      <Upload className="w-4 h-4 mr-2" />
                      {t('driver_upload_document')}
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {requiredDocuments.map((docType) => {
                      const doc = documents.find(d => d.document_type === docType);
                      const status = doc ? getDocumentStatus(doc, t) : null;
                      const StatusIcon = status?.icon;
                      
                      return (
                        <div key={docType} className="border rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-900 text-sm">{getDocumentName(docType, t)}</h3>
                              {doc ? (
                                <div className="mt-1 space-y-1">
                                  <div className="flex items-center gap-2">
                                    {status && <StatusIcon className={`w-4 h-4 ${status.color}`} />}
                                    <span className={`text-xs font-medium ${status?.color}`}>
                                      {status?.text}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    {t('driver_doc_uploaded')} {new Date(doc.upload_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                                  </p>
                                  {doc.expiry_date && (
                                    <p className="text-xs text-gray-600">
                                      {t('driver_doc_expires')} {new Date(doc.expiry_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                                    </p>
                                  )}
                                  {doc.rejection_reason && (
                                    <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded">
                                      <p className="text-xs text-red-800">
                                        <strong>{t('driver_rejection_reason')}</strong> {doc.rejection_reason}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="mt-1 text-xs text-gray-500">{t('driver_doc_not_uploaded')}</p>
                              )}
                            </div>
                            {doc && (doc.status === 'rejected' || doc.status === 'expired') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setUploadForm({
                                    ...uploadForm,
                                    document_type: docType
                                  });
                                  setShowUploadDialog(true);
                                }}
                                className="ml-2 h-6 text-xs"
                              >
                                {t('driver_update')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Fleet Members Documents - Only for fleet owners */}
                {driver?.is_fleet_driver && fleetMembers.length > 0 && (
                  <div className="pt-6 mt-6 border-t">
                    <Label className="text-lg font-semibold mb-4 block">{t('driver_fleet_docs_title')}</Label>
                    <div className="space-y-6">
                      {fleetMembers.map((member) => (
                        <div key={member.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-gray-900">{member.name}</h3>
                              <p className="text-sm text-gray-600">{member.email}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              member.documents_validated ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {member.documents_validated ? t('driver_fleet_validated') : `⏳ ${t('driver_doc_pending')}`}
                            </span>
                          </div>
                          
                          {/* Load and display member documents */}
                          <MemberDocumentsSection memberId={member.id} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab - will continue */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{t('driver_my_documents')}</CardTitle>
                    <CardDescription>
                      {t('driver_docs_progress', { uploaded: getDocumentProgress().uploaded, total: getDocumentProgress().total })}
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowUploadDialog(true)} className="bg-[#36e961] hover:bg-[#2dd156]">
                    <Upload className="w-4 h-4 mr-2" />
                    {t('driver_upload_document')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {requiredDocuments.map((docType) => {
                    const doc = documents.find(d => d.document_type === docType);
                    const status = doc ? getDocumentStatus(doc, t) : null;
                    const StatusIcon = status?.icon;
                    
                    return (
                      <div key={docType} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{getDocumentName(docType, t)}</h3>
                            {doc ? (
                              <div className="mt-2 space-y-1">
                                <div className="flex items-center gap-2">
                                  {status && <StatusIcon className={`w-4 h-4 ${status.color}`} />}
                                  <span className={`text-sm font-medium ${status?.color}`}>
                                    {status?.text}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {t('driver_doc_uploaded')} {new Date(doc.upload_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                                </p>
                                {doc.expiry_date && (
                                  <p className="text-sm text-gray-600">
                                    {t('driver_doc_expires')} {new Date(doc.expiry_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                                  </p>
                                )}
                                {doc.rejection_reason && (
                                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                    <p className="text-sm text-red-800">
                                      <strong>{t('driver_rejection_reason')}</strong> {doc.rejection_reason}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="mt-1 text-sm text-gray-500">{t('driver_doc_not_uploaded')}</p>
                            )}
                          </div>
                          {doc && (doc.status === 'rejected' || doc.status === 'expired') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setUploadForm({
                                  ...uploadForm,
                                  document_type: docType
                                });
                                setShowUploadDialog(true);
                              }}
                            >
                              {t('driver_update')}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('driver_billing_my_settlements')}</CardTitle>
                    <CardDescription>{t('driver_billing_subtitle')}</CardDescription>
                  </div>
                  <Button onClick={() => { loadBillingPeriods(); loadCurrentPeriod(); }} disabled={loadingBilling || loadingCurrentPeriod} size="sm">
                    {(loadingBilling || loadingCurrentPeriod) ? t('driver_loading') : t('driver_update')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Info banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    {t('driver_billing_periods_info')}
                  </p>
                </div>

                {/* CURRENT PERIOD - EN CURSO */}
                {loadingCurrentPeriod ? (
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full"></div>
                      <span className="text-green-700">Cargando período actual...</span>
                    </div>
                  </div>
                ) : currentPeriodData && currentPeriodData.has_current_period ? (
                  <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-lg">📊</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-green-800 text-lg flex items-center gap-2">
                            {t('driver_billing_in_progress')}
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          </h3>
                          <p className="text-sm text-green-600">
                            {currentPeriodData.period_type === 'quincena_1' ? t('driver_billing_fortnight_1') : t('driver_billing_fortnight_2')}: {new Date(currentPeriodData.period_start).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')} - {new Date(currentPeriodData.period_end).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                          </p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-medium">
                        {t('driver_billing_payment_label')} {new Date(currentPeriodData.payment_start).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')} - {new Date(currentPeriodData.payment_end).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                      </span>
                    </div>
                    
                    {/* Current period totals */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_services')}</p>
                        <p className="text-xl font-bold text-green-700">{currentPeriodData.total_trips}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_base')}</p>
                        <p className="text-lg font-semibold text-gray-700">€{currentPeriodData.base_imponible.toFixed(2)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_iva')}</p>
                        <p className="text-lg font-semibold text-gray-600">€{currentPeriodData.iva_amount.toFixed(2)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_supplies')}</p>
                        <p className="text-lg font-semibold text-orange-600">€{currentPeriodData.gastos_suplidos.toFixed(2)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border">
                        <p className="text-xs text-gray-500 mb-1">{t('driver_billing_irpf_label')} ({currentPeriodData.irpf_percentage || 0}%)</p>
                        <p className="text-lg font-semibold text-red-600">€{currentPeriodData.irpf_amount.toFixed(2)}</p>
                      </div>
                      <div className="bg-green-100 rounded-lg p-3 text-center border-2 border-green-400 col-span-2">
                        <p className="text-xs text-green-600 mb-1">{t('driver_billing_total_accumulated')}</p>
                        <p className="text-2xl font-bold text-green-700">€{currentPeriodData.net_amount.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    {/* Fleet breakdown if fleet owner */}
                    {currentPeriodData.is_fleet_owner && currentPeriodData.fleet_breakdown && Object.keys(currentPeriodData.fleet_breakdown).length > 0 && (
                      <div className="bg-white rounded-lg p-3 mb-3 border">
                        <p className="text-sm font-semibold text-gray-700 mb-2">👥 {t('driver_billing_fleet_breakdown')}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {Object.values(currentPeriodData.fleet_breakdown).map((member, idx) => (
                            <div key={idx} className="bg-blue-50 rounded p-2 text-sm">
                              <p className="font-medium text-gray-800 truncate">{member.name}</p>
                              <p className="text-gray-600">{member.trips} {t('driver_billing_trips')} • €{member.total.toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Toggle services details */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCurrentPeriodDetails(!showCurrentPeriodDetails)}
                      className="w-full border-green-400 text-green-700 hover:bg-green-100"
                    >
                      {showCurrentPeriodDetails ? t('driver_billing_hide_services') : t('driver_billing_view_services', { count: currentPeriodData.total_trips })}
                    </Button>
                    
                    {/* Services details table */}
                    {showCurrentPeriodDetails && currentPeriodData.services && currentPeriodData.services.length > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
<thead>
                          <tr className="bg-green-100">
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_table_date')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_plate')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_table_vehicle')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_table_trip')}</th>
                              {currentPeriodData.is_fleet_owner && (
                                <th className="text-left py-2 px-3 font-medium text-gray-700">{t('driver_billing_driver')}</th>
                              )}
                              <th className="text-right py-2 px-3 font-medium text-gray-700">{t('driver_billing_rate')}</th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">{t('driver_billing_supplies')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentPeriodData.services.map((service, idx) => (
                              <tr key={service.id || idx} className="border-b hover:bg-green-50">
                                <td className="py-2 px-3">
                                  {service.pickup_date_from ? new Date(service.pickup_date_from).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { day: '2-digit', month: 'short' }) : '-'}
                                </td>
                                <td className="py-2 px-3">
                                  <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{service.car_plate || '-'}</span>
                                </td>
                                <td className="py-2 px-3 text-gray-600">
                                  {service.car_brand} {service.car_model}
                                </td>
                                <td className="py-2 px-3">
                                  <div className="max-w-xs">
                                    <div className="flex items-center gap-1 text-xs">
                                      <span className="text-green-600">●</span>
                                      <span className="truncate" title={service.pickup_address}>{service.pickup_address?.split(',')[0] || '-'}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs mt-0.5">
                                      <span className="text-red-600">●</span>
                                      <span className="truncate" title={service.delivery_address}>{service.delivery_address?.split(',')[0] || '-'}</span>
                                    </div>
                                  </div>
                                </td>
                                {currentPeriodData.is_fleet_owner && (
                                  <td className="py-2 px-3 text-gray-700">{service.driver_name || '-'}</td>
                                )}
                                <td className="py-2 px-3 text-right font-medium text-gray-800">€{(service.driver_price || 0).toFixed(2)}</td>
                                <td className="py-2 px-3 text-right text-orange-600">€{(service.expenses || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-green-100 font-semibold">
                              <td colSpan={currentPeriodData.is_fleet_owner ? 5 : 4} className="py-2 px-3 text-right text-gray-700">{t('driver_billing_totals')}</td>
                              <td className="py-2 px-3 text-right text-gray-800">€{currentPeriodData.base_imponible.toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-orange-600">€{currentPeriodData.gastos_suplidos.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                ) : currentPeriodData && !currentPeriodData.has_current_period ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 text-lg">📭</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-700">{t('driver_billing_current_no_services')}</h3>
                        <p className="text-sm text-gray-500">{t('driver_billing_current_no_services_desc')}</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* HISTORICAL PERIODS */}
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  📅 {t('driver_billing_previous_periods')}
                </h3>

                {loadingBilling && billingPeriods.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-3 text-gray-500">{t('driver_billing_loading_settlements')}</p>
                  </div>
                ) : billingPeriods.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-semibold mb-1">{t('driver_billing_no_previous')}</p>
                    <p className="text-sm">{t('driver_billing_no_previous_desc')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {billingPeriods.map((period) => {
                      const isPaid = period.status === 'paid';
                      
                      return (
                        <div 
                          key={period.id}
                          className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                  {period.period_type === 'quincena_1' ? t('driver_billing_fortnight_1') : t('driver_billing_fortnight_2')}
                                </span>
                                <h3 className="font-semibold text-sm">
                                  {new Date(period.period_start).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')} - {new Date(period.period_end).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                                </h3>
                              </div>
                              <div className="text-xs text-gray-600">
                                <span className="font-semibold">{t('driver_billing_payment_label')}</span> {period.payment_start ? new Date(period.payment_start).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB') : '?'} - {period.payment_end ? new Date(period.payment_end).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB') : '?'}
                              </div>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {isPaid ? t('driver_billing_paid') : `⏳ ${t('driver_doc_pending')}`}
                            </div>
                          </div>

                          {/* Financial breakdown */}
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3 text-center">
                            <div className="bg-blue-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_services')}</div>
                              <div className="text-lg font-bold text-blue-700">{period.total_trips}</div>
                            </div>
                            <div className="bg-green-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_base')}</div>
                              <div className="text-sm font-bold text-green-700">€{(period.base_imponible || period.total_revenue)?.toFixed(2)}</div>
                            </div>
                            <div className="bg-teal-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_iva').replace(' (21%)', '')}</div>
                              <div className="text-sm font-bold text-teal-700">€{(period.iva_amount || 0)?.toFixed(2)}</div>
                            </div>
                            <div className="bg-orange-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_supplies')}</div>
                              <div className="text-sm font-bold text-orange-700">€{(period.gastos_suplidos || period.total_expenses)?.toFixed(2)}</div>
                            </div>
                            <div className="bg-red-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('driver_billing_irpf_label')}</div>
                              <div className="text-sm font-bold text-red-700">€{(period.irpf_amount || 0)?.toFixed(2)}</div>
                            </div>
                            <div className="bg-purple-50 rounded p-2 border-2 border-purple-300">
                              <div className="text-xs text-gray-600">TOTAL</div>
                              <div className="text-lg font-bold text-purple-700">€{period.net_amount?.toFixed(2)}</div>
                            </div>
                          </div>

                          {/* Fleet breakdown if exists */}
                          {period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0 && (
                            <div className="bg-gray-50 rounded p-2 mb-3">
                              <p className="text-xs text-gray-600 mb-1">{t('driver_billing_period_breakdown')}</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(period.fleet_breakdown).map(([driverId, data]) => (
                                  <span key={driverId} className="text-xs bg-white px-2 py-1 rounded border">
                                    {data.driver_name}: {data.services?.length || 0} {t('driver_billing_services_count')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => viewPeriodDetails(period)}
                              className="flex-1"
                            >
                              <Eye className="w-3 h-3 mr-2" />
                              {t('driver_billing_view_services_btn')}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                const token = localStorage.getItem('driver_token');
                                window.open(`${BACKEND_URL}/api/drivers/liquidations/${period.id}/excel?token=${token}`, '_blank');
                              }}
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              data-testid={`download-excel-${period.id}`}
                            >
                              <Download className="w-3 h-3 mr-2" />
                              Excel
                            </Button>
                            {period.invoice_generated && period.invoice_pdf_url && (
                              <Button 
                                size="sm"
                                onClick={() => window.open(`${BACKEND_URL}${period.invoice_pdf_url}`, '_blank')}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <FileText className="w-3 h-3 mr-2" />
                                {t('driver_billing_download_invoice')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Period Details Dialog */}
      <Dialog open={showPeriodDialog} onOpenChange={setShowPeriodDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('driver_billing_period_details_title')}</DialogTitle>
            <DialogDescription>
              {selectedPeriod && (
                <>
                  {t('driver_billing_period_label')} {new Date(selectedPeriod.period_start).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')} - {new Date(selectedPeriod.period_end).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB')}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {periodDetails && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-blue-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">{t('driver_billing_total_trips')}</div>
                  <div className="text-2xl font-bold text-blue-700">{periodDetails.period.total_trips}</div>
                </div>
                <div className="bg-green-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">Ingresos (sin IVA)</div>
                  <div className="text-2xl font-bold text-green-700">€{periodDetails.period.total_revenue?.toFixed(2)}</div>
                </div>
                <div className="bg-teal-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">IVA (21%)</div>
                  <div className="text-2xl font-bold text-teal-700">€{(periodDetails.period.total_revenue * 0.21)?.toFixed(2)}</div>
                </div>
                <div className="bg-orange-50 rounded p-3 text-center">
                  <div className="text-xs text-gray-600 mb-1">Gastos (sin IVA)</div>
                  <div className="text-2xl font-bold text-orange-700">€{periodDetails.period.total_expenses?.toFixed(2)}</div>
                </div>
                <div className="bg-purple-50 rounded p-3 text-center border-2 border-purple-300">
                  <div className="text-xs text-gray-600 mb-1">Total to Pay</div>
                  <div className="text-2xl font-bold text-purple-700">€{((periodDetails.period.total_revenue * 1.21) + periodDetails.period.total_expenses)?.toFixed(2)}</div>
                  <div className="text-[9px] text-gray-500 mt-1">(Ingresos + IVA + Gastos)</div>
                </div>
              </div>

              {/* Orders List */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Servicios Completados ({periodDetails.orders?.length || 0})
                </h3>
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">{t('driver_date_pickup')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_date_delivery_label')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_billing_driver')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_origin_short')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_destination_short')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_plate')}</th>
                        <th className="px-3 py-2 text-left">{t('driver_table_vehicle')}</th>
                        <th className="px-3 py-2 text-right">{t('driver_table_price')}</th>
                        <th className="px-3 py-2 text-center">{t('driver_expenses_label')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodDetails.orders?.map((order) => {
                        const expenseReport = periodDetails.expense_reports?.find(r => r.order_id === order.id);
                        
                        return (
                          <tr key={order.id} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div>
                                {order.pickup_date ? new Date(order.pickup_date).toLocaleDateString('es-ES') : 
                                 order.service_date ? new Date(order.service_date).toLocaleDateString('es-ES') : 'N/A'}
                              </div>
                              {(order.pickup_time_start || order.pickup_time_end) && (
                                <div className="text-gray-500 text-[10px] mt-0.5">
                                  🕐 {order.pickup_time_start && order.pickup_time_end 
                                    ? `${order.pickup_time_start} - ${order.pickup_time_end}`
                                    : order.pickup_time_start || order.pickup_time_end}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div>
                                {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('es-ES') : 
                                 order.delivery_margin_date ? new Date(order.delivery_margin_date).toLocaleDateString('es-ES') :
                                 order.service_date ? new Date(order.service_date).toLocaleDateString('es-ES') : 'N/A'}
                              </div>
                              {(order.delivery_time_start || order.delivery_time_end) && (
                                <div className="text-gray-500 text-[10px] mt-0.5">
                                  🕐 {order.delivery_time_start && order.delivery_time_end 
                                    ? `${order.delivery_time_start} - ${order.delivery_time_end}`
                                    : order.delivery_time_start || order.delivery_time_end}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">{order.driver_name || 'N/A'}</td>
                            <td className="px-3 py-2">{order.pickup_address?.split(',')[0]}</td>
                            <td className="px-3 py-2">{order.delivery_address?.split(',')[0]}</td>
                            <td className="px-3 py-2 font-mono text-[10px]">{order.car_plate || 'N/A'}</td>
                            <td className="px-3 py-2 text-[10px]">
                              {order.car_brand && order.car_model ? `${order.car_brand} ${order.car_model}` : 'N/A'}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                              {(isFleetEmployee || order._prices_hidden) ? '—' : `€${order.driver_price?.toFixed(2) || '0.00'}`}
                              {!isFleetEmployee && !order._prices_hidden && order.driver_pricing_template_id && (
                                <div className="text-[10px] text-gray-600">
                                  {driverPricingTemplates.find(t => t.id === order.driver_pricing_template_id)?.name}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {expenseReport ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
                                  expenseReport.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  expenseReport.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  <Receipt className="w-3 h-3" />
                                  €{expenseReport.total_expense?.toFixed(2)}
                                  {expenseReport.status === 'approved' && ' ✓'}
                                  {expenseReport.status === 'rejected' && ' ✗'}
                                  {expenseReport.status === 'pending' && ' ⏳'}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Expense Reports Details */}
              {periodDetails.expense_reports && periodDetails.expense_reports.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    {t('driver_expense_details_title')}
                  </h3>
                  <div className="space-y-2">
                    {periodDetails.expense_reports.map((report) => {
                      const order = periodDetails.orders?.find(o => o.id === report.order_id);
                      const isExpanded = expandedReceipts[report.id] || false;
                      
                      return (
                        <div key={report.id} className="border rounded-lg p-3 text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-semibold">{order?.pickup_address?.split(',')[0]} → {order?.delivery_address?.split(',')[0]}</div>
                              <div className="text-gray-600">{new Date(order?.service_date).toLocaleDateString('es-ES')}</div>
                            </div>
                            <span className={`px-2 py-1 rounded text-[10px] font-semibold ${
                              report.status === 'approved' ? 'bg-green-100 text-green-700' :
                              report.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {report.status === 'approved' ? t('driver_doc_status_approved') :
                               report.status === 'rejected' ? t('driver_rejected') : t('service_status_pending')}
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-[10px] text-gray-600">{t('driver_billing_driver')}</div>
                              <div className="font-semibold">€{report.driver_expense?.toFixed(2)}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-[10px] text-gray-600">{t('driver_expense_fuel')}</div>
                              <div className="font-semibold">€{report.fuel_expense?.toFixed(2)}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-[10px] text-gray-600">{t('driver_expense_tolls')}</div>
                              <div className="font-semibold">€{report.toll_expense?.toFixed(2)}</div>
                            </div>
                            <div className="bg-blue-50 rounded p-2 flex items-center justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedReceipts(prev => ({...prev, [report.id]: !prev[report.id]}))}
                                className="h-auto py-1 px-2 text-[10px] text-blue-700 hover:bg-blue-100"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                {t('driver_view_more')}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Expanded Receipts Section */}
                          {isExpanded && (
                            <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                              <h4 className="font-semibold text-[11px] mb-2 flex items-center gap-1">
                                <Image className="w-3 h-3" />
                                {t('driver_expense_tickets')}
                              </h4>
                              {report.receipts && report.receipts.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {report.receipts.map((receipt, idx) => (
                                    <div key={idx} className="border rounded overflow-hidden bg-white">
                                      <img 
                                        src={receipt} 
                                        alt={`Ticket ${idx + 1}`}
                                        className="w-full h-32 object-cover cursor-pointer hover:opacity-80"
                                        onClick={() => window.open(receipt, '_blank')}
                                      />
                                      <div className="p-1 text-center text-[9px] text-gray-600">
                                        Ticket {idx + 1}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-4 text-gray-400 text-[10px]">
                                  No hay tickets adjuntos
                                </div>
                              )}
                              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                                <div className="bg-white rounded p-2">
                                  <span className="text-gray-600">Total Parking:</span>
                                  <span className="font-semibold ml-1">€{report.parking_expense?.toFixed(2)}</span>
                                </div>
                                <div className="bg-white rounded p-2">
                                  <span className="text-gray-600">Total Gastos:</span>
                                  <span className="font-semibold ml-1">€{report.total_expense?.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {report.status === 'rejected' && report.rejection_reason && (
                            <div className="mt-2 p-2 bg-red-50 rounded text-red-700">
                              <span className="font-semibold">Razón de rechazo:</span> {report.rejection_reason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog
        open={showUploadDialog}
        onOpenChange={(open) => {
          setShowUploadDialog(open);
          setUploadFileError('');
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('driver_upload_document')}</DialogTitle>
            <DialogDescription>
              {t('driver_upload_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDocumentUpload} className="space-y-4">
            <div>
              <Label>{t('driver_doc_type_label')}</Label>
              <select
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                value={uploadForm.document_type}
                onChange={(e) => {
                  setUploadFileError('');
                  setUploadForm({ ...uploadForm, document_type: e.target.value, file: null });
                }}
                required
              >
                <option value="">{t('driver_doc_type_placeholder')}</option>
                {requiredDocuments.map(docType => (
                  <option key={docType} value={docType}>
                    {getDocumentName(docType, t)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>{t('driver_file_label')}</Label>
              <Input
                type="file"
                accept={uploadForm.document_type?.endsWith('_certificate') ? '.pdf' : 'image/*'}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) {
                    setUploadFileError('');
                    setUploadForm({ ...uploadForm, file: null });
                    return;
                  }
                  if (f.size > DRIVER_DOCUMENT_MAX_BYTES) {
                    const msg = t('driver_error_file_too_large', { maxMB: DRIVER_DOCUMENT_MAX_MB });
                    setUploadFileError(msg);
                    toast.error(msg);
                    e.target.value = '';
                    setUploadForm({ ...uploadForm, file: null });
                    return;
                  }
                  setUploadFileError('');
                  setUploadForm({ ...uploadForm, file: f });
                }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {uploadForm.document_type?.endsWith('_certificate')
                  ? t('driver_file_hint_pdf', { maxMB: DRIVER_DOCUMENT_MAX_MB })
                  : t('driver_file_hint_image', { maxMB: DRIVER_DOCUMENT_MAX_MB })}
              </p>
              {uploadFileError ? (
                <p className="text-xs text-red-600 mt-1" role="alert">
                  {uploadFileError}
                </p>
              ) : null}
            </div>

            <div>
              <Label>{t('driver_upload_date_label')}</Label>
              <Input
                type="date"
                value={uploadForm.upload_date}
                onChange={(e) => setUploadForm({ ...uploadForm, upload_date: e.target.value })}
                required
              />
            </div>

            {(uploadForm.document_type === 'hacienda_certificate' || 
              uploadForm.document_type === 'ss_certificate') && (
              <div>
                <Label>{t('driver_expiry_date_label')}</Label>
                <Input
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {uploadForm.document_type === 'hacienda_certificate'
                    ? t('driver_expiry_hint_hacienda')
                    : t('driver_expiry_hint_ss')}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={uploadingDoc} className="bg-[#36e961] hover:bg-[#2dd156]">
                {uploadingDoc ? t('driver_uploading') : t('driver_upload_btn')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Fleet Member Dialog */}
      {driver?.is_fleet_driver && (
        <Dialog open={showCreateMemberDialog} onOpenChange={setShowCreateMemberDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
<DialogTitle>{t('driver_dialog_add_fleet_title')}</DialogTitle>
            <DialogDescription>
                {t('driver_dialog_add_fleet_desc')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateFleetMember} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_dialog_basic_info')}</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="member_name">{t('driver_dialog_full_name')} *</Label>
                    <Input
                      id="member_name"
                      type="text"
                      value={createMemberForm.name}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_email">Email *</Label>
                    <Input
                      id="member_email"
                      type="email"
                      value={createMemberForm.email}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_phone">{t('driver_label_phone')} *</Label>
                    <Input
                      id="member_phone"
                      type="tel"
                      value={createMemberForm.phone}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, phone: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_license">{t('driver_dialog_license_no')} *</Label>
                    <Input
                      id="member_license"
                      type="text"
                      value={createMemberForm.license_number}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, license_number: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="member_dni_nie">{t('driver_billing_dni')} *</Label>
                    <Input
                      id="member_dni_nie"
                      type="text"
                      value={createMemberForm.dni_nie}
                      onChange={(e) => setCreateMemberForm({ ...createMemberForm, dni_nie: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    {t('driver_dialog_password_note')}
                  </p>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_dialog_docs_optional')}</h3>
                <p className="text-sm text-gray-600">
                  {t('driver_dialog_docs_later')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* License Front */}
                  <div>
                    <Label htmlFor="license_front">Carnet de Conducir (Frontal)</Label>
                    <div className="mt-2">
                      <input
                        id="license_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, license_front: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.license_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.license_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* License Back */}
                  <div>
                    <Label htmlFor="license_back">Carnet de Conducir (Reverso)</Label>
                    <div className="mt-2">
                      <input
                        id="license_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, license_back: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.license_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.license_back.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Front */}
                  <div>
                    <Label htmlFor="dni_front">DNI/NIE (Frontal)</Label>
                    <div className="mt-2">
                      <input
                        id="dni_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, dni_front: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.dni_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.dni_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Back */}
                  <div>
                    <Label htmlFor="dni_back">DNI/NIE (Reverso)</Label>
                    <div className="mt-2">
                      <input
                        id="dni_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setMemberDocuments({ ...memberDocuments, dni_back: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {memberDocuments.dni_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {memberDocuments.dni_back.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowCreateMemberDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-[#36e961] hover:bg-[#2dd156]">
                  {t('driver_dialog_create_driver')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Fleet Member Dialog */}
      {driver?.is_fleet_driver && editingMember && (
        <Dialog open={showEditMemberDialog} onOpenChange={setShowEditMemberDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
<DialogTitle>{t('driver_dialog_edit_fleet_title')}</DialogTitle>
            <DialogDescription>
                {t('driver_dialog_edit_fleet_desc')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateMember} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold">Información Básica</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_member_name">Nombre Completo *</Label>
                    <Input
                      id="edit_member_name"
                      type="text"
                      value={editingMember.name}
                      onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_member_email">Email *</Label>
                    <Input
                      id="edit_member_email"
                      type="email"
                      value={editingMember.email}
                      onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_member_phone">Teléfono *</Label>
                    <Input
                      id="edit_member_phone"
                      type="tel"
                      value={editingMember.phone}
                      onChange={(e) => setEditingMember({ ...editingMember, phone: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_member_license">Nº Carnet de Conducir *</Label>
                    <Input
                      id="edit_member_license"
                      type="text"
                      value={editingMember.license_number}
                      onChange={(e) => setEditingMember({ ...editingMember, license_number: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="edit_member_available"
                    checked={editingMember.is_available}
                    onChange={(e) => setEditingMember({ ...editingMember, is_available: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit_member_available" className="text-sm cursor-pointer">
                    {t('driver_dialog_driver_available')}
                  </Label>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="space-y-4">
                <h3 className="font-semibold">{t('driver_upload_document')}</h3>
                <p className="text-sm text-gray-600">
                  {t('driver_fleet_edit_docs_intro', { maxMB: DRIVER_DOCUMENT_MAX_MB })}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* License Front */}
                  <div>
                    <Label htmlFor="edit_license_front">{t('driver_doc_license_front')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_license_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = pickFleetMemberDocumentFile(e.target.files[0], e.target);
                          setEditingMember({
                            ...editingMember,
                            documents: { ...editingMember.documents, license_front: file }
                          });
                        }}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.license_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.license_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* License Back */}
                  <div>
                    <Label htmlFor="edit_license_back">{t('driver_doc_license_back')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_license_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = pickFleetMemberDocumentFile(e.target.files[0], e.target);
                          setEditingMember({
                            ...editingMember,
                            documents: { ...editingMember.documents, license_back: file }
                          });
                        }}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.license_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.license_back.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Front */}
                  <div>
                    <Label htmlFor="edit_dni_front">{t('driver_doc_dni_front')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_dni_front"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = pickFleetMemberDocumentFile(e.target.files[0], e.target);
                          setEditingMember({
                            ...editingMember,
                            documents: { ...editingMember.documents, dni_front: file }
                          });
                        }}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.dni_front && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.dni_front.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DNI Back */}
                  <div>
                    <Label htmlFor="edit_dni_back">{t('driver_doc_dni_back')}</Label>
                    <div className="mt-2">
                      <input
                        id="edit_dni_back"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = pickFleetMemberDocumentFile(e.target.files[0], e.target);
                          setEditingMember({
                            ...editingMember,
                            documents: { ...editingMember.documents, dni_back: file }
                          });
                        }}
                        className="w-full text-sm"
                      />
                      {editingMember.documents.dni_back && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {editingMember.documents.dni_back.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditMemberDialog(false);
                  setEditingMember(null);
                }}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-[#36e961] hover:bg-[#2dd156]">
                  {t('driver_save_changes')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para completar servicio - SIMPLIFICADO */}
      {showCompleteServiceDialog && selectedOrderToComplete && (
        <Dialog open={showCompleteServiceDialog} onOpenChange={setShowCompleteServiceDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {t('driver_complete_dialog_title', {
                  id: selectedOrderToComplete.id?.substring(0, 8).toUpperCase(),
                })}
              </DialogTitle>
              <DialogDescription>
                {selectedOrderToComplete.car_brand} {selectedOrderToComplete.car_model} - {selectedOrderToComplete.car_plate}
              </DialogDescription>
            </DialogHeader>

            {renderDriverOrderNotesAndAttachments(selectedOrderToComplete)}

            <div className="space-y-6">
              {/* SECCIÓN 1: Fotos de Recogida */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-blue-700">
                    <Image className="w-5 h-5" />
                    {t('driver_complete_section_pickup')}
                  </h3>
                  {selectedOrderToComplete.has_pickup_photos && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_pickup_photos ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_pickup_hint')}
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const compressedPhotos = await processFilesWithCompression(files);
                        setPickupPhotos(compressedPhotos);
                      }}
                      className="bg-white"
                    />
                    {pickupPhotos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {pickupPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`${t('driver_complete_summary_pickup')} ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                            <button
                              onClick={() => setPickupPhotos(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                            >×</button>
                          </div>
                        ))}
                        <span className="text-sm text-gray-500 self-center">
                          {t('driver_complete_photo_count', { count: pickupPhotos.length })}
                        </span>
                      </div>
                    )}
                    <Button
                      onClick={async () => {
                        if (pickupPhotos.length === 0) {
                          toast.error(t('driver_toast_select_at_least_one_photo'));
                          return;
                        }
                        setUploadingPickupPhotos(true);
                        try {
                          const token = localStorage.getItem('driver_token');
                          const response = await fetch(`${BACKEND_URL}/api/orders/${selectedOrderToComplete.id}/simple-photos`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              photo_type: 'pickup',
                              photos: pickupPhotos
                            })
                          });
                          if (response.ok) {
                            toast.success(t('driver_toast_pickup_photos_uploaded'));
                            // Update local state immediately
                            setSelectedOrderToComplete(prev => ({...prev, has_pickup_photos: true}));
                            setPickupPhotos([]);
                            // Reload incomplete orders in background (don't wait)
                            loadIncompleteOrders().catch(console.error);
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || t('driver_toast_error_upload_photos'));
                            console.error('Error response:', err);
                          }
                        } catch (error) {
                          toast.error(t('driver_toast_error_upload_pickup_photos'));
                        } finally {
                          setUploadingPickupPhotos(false);
                        }
                      }}
                      disabled={uploadingPickupPhotos || pickupPhotos.length === 0}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {uploadingPickupPhotos ? t('driver_uploading') : t('driver_upload_pickup_photos', { count: pickupPhotos.length })}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_pickup_done')}</p>
                )}
              </div>

              {/* SECCIÓN 2: Fotos de Entrega */}
              <div className="border rounded-lg p-4 bg-green-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-green-700">
                    <Image className="w-5 h-5" />
                    {t('driver_complete_section_delivery')}
                  </h3>
                  {selectedOrderToComplete.has_delivery_photos && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_delivery_photos ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_delivery_hint')}
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const compressedPhotos = await processFilesWithCompression(files);
                        setDeliveryPhotos(compressedPhotos);
                      }}
                      className="bg-white"
                    />
                    {deliveryPhotos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {deliveryPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`${t('driver_complete_summary_delivery')} ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                            <button
                              onClick={() => setDeliveryPhotos(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                            >×</button>
                          </div>
                        ))}
                        <span className="text-sm text-gray-500 self-center">
                          {t('driver_complete_photo_count', { count: deliveryPhotos.length })}
                        </span>
                      </div>
                    )}
                    <Button
                      onClick={async () => {
                        if (deliveryPhotos.length === 0) {
                          toast.error(t('driver_toast_select_at_least_one_photo'));
                          return;
                        }
                        setUploadingDeliveryPhotos(true);
                        try {
                          const token = localStorage.getItem('driver_token');
                          const response = await fetch(`${BACKEND_URL}/api/orders/${selectedOrderToComplete.id}/simple-photos`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              photo_type: 'delivery',
                              photos: deliveryPhotos
                            })
                          });
                          if (response.ok) {
                            toast.success(t('driver_toast_delivery_photos_uploaded'));
                            // Update local state immediately
                            setSelectedOrderToComplete(prev => ({...prev, has_delivery_photos: true}));
                            setDeliveryPhotos([]);
                            // Reload incomplete orders in background (don't wait)
                            loadIncompleteOrders().catch(console.error);
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || t('driver_toast_error_upload_photos'));
                            console.error('Error response:', err);
                          }
                        } catch (error) {
                          toast.error(t('driver_toast_error_upload_delivery_photos'));
                        } finally {
                          setUploadingDeliveryPhotos(false);
                        }
                      }}
                      disabled={uploadingDeliveryPhotos || deliveryPhotos.length === 0}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {uploadingDeliveryPhotos ? t('driver_uploading') : t('driver_upload_delivery_photos', { count: deliveryPhotos.length })}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_delivery_done')}</p>
                )}
              </div>

              {/* SECCIÓN 3: Contrato Firmado */}
              <div className="border rounded-lg p-4 bg-purple-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-purple-700">
                    <FileText className="w-5 h-5" />
                    {t('driver_complete_section_contract')}
                  </h3>
                  {selectedOrderToComplete.has_contract_photos && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_contract_photos ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_contract_hint')}
                    </p>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const compressedPhotos = await processFilesWithCompression(files);
                        setContractPhotos(compressedPhotos);
                      }}
                      className="bg-white"
                    />
                    {contractPhotos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {contractPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`${t('driver_complete_summary_contract')} ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                            <button
                              onClick={() => setContractPhotos(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                            >×</button>
                          </div>
                        ))}
                        <span className="text-sm text-gray-500 self-center">
                          {t('driver_complete_photo_count', { count: contractPhotos.length })}
                        </span>
                      </div>
                    )}
                    <Button
                      onClick={async () => {
                        if (contractPhotos.length === 0) {
                          toast.error(t('driver_toast_select_contract_photo'));
                          return;
                        }
                        setUploadingContractPhotos(true);
                        try {
                          const token = localStorage.getItem('driver_token');
                          const response = await fetch(`${BACKEND_URL}/api/orders/${selectedOrderToComplete.id}/simple-photos`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              photo_type: 'contract',
                              photos: contractPhotos
                            })
                          });
                          if (response.ok) {
                            toast.success(t('driver_toast_contract_uploaded'));
                            // Update local state immediately
                            setSelectedOrderToComplete(prev => ({...prev, has_contract_photos: true}));
                            setContractPhotos([]);
                            // Reload incomplete orders in background (don't wait)
                            loadIncompleteOrders().catch(console.error);
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || t('driver_toast_error_upload_contract'));
                            console.error('Error response:', err);
                          }
                        } catch (error) {
                          toast.error(t('driver_toast_error_upload_contract'));
                        } finally {
                          setUploadingContractPhotos(false);
                        }
                      }}
                      disabled={uploadingContractPhotos || contractPhotos.length === 0}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {uploadingContractPhotos ? t('driver_uploading') : t('driver_upload_contract_photos', { count: contractPhotos.length })}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_contract_done')}</p>
                )}
              </div>

              {/* SECCIÓN 4: Reporte de Gastos */}
              <div className="border rounded-lg p-4 bg-orange-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-orange-700">
                    <DollarSign className="w-5 h-5" />
                    {t('driver_complete_section_expenses')}
                  </h3>
                  {selectedOrderToComplete.has_expense_report && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {t('driver_complete_status_done')}
                    </span>
                  )}
                </div>
                
                {!selectedOrderToComplete.has_expense_report ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {t('driver_complete_expense_intro')}
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm">{t('driver_expense_fuel')} (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.fuel_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, fuel_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_tolls')} (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.toll_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, toll_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_taxi')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.taxi_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, taxi_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_parking')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.parking_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, parking_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t('driver_expense_other_label')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.other_expense}
                          onChange={(e) => setExpenseForm(prev => ({...prev, other_expense: parseFloat(e.target.value) || 0}))}
                          className="bg-white"
                        />
                      </div>
                      {expenseForm.other_expense > 0 && (
                        <div>
                          <Label className="text-sm">{t('driver_expense_other_description')}</Label>
                          <Input
                            type="text"
                            value={expenseForm.other_expense_description}
                            onChange={(e) => setExpenseForm(prev => ({...prev, other_expense_description: e.target.value}))}
                            placeholder={t('ph_describe_expense')}
                            className="bg-white"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Fotos de Facturas */}
                    <div className="border-t pt-3 mt-3">
                      <Label className="text-sm font-medium">{t('driver_complete_receipt_photos_label')}</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          const compressedPhotos = await processFilesWithCompression(files);
                          setReceiptPhotos(compressedPhotos);
                          setExpenseForm(prev => ({...prev, receipts: compressedPhotos}));
                        }}
                        className="mt-1 bg-white"
                      />
                      {receiptPhotos.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {receiptPhotos.map((photo, idx) => (
                            <div key={idx} className="relative">
                              <img src={photo} alt={`${t('driver_expense_tickets')} ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                              <button
                                onClick={() => {
                                  const newPhotos = receiptPhotos.filter((_, i) => i !== idx);
                                  setReceiptPhotos(newPhotos);
                                  setExpenseForm(prev => ({...prev, receipts: newPhotos}));
                                }}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                              >×</button>
                            </div>
                          ))}
                          <span className="text-sm text-gray-500 self-center">
                            {t('driver_complete_invoice_count', { count: receiptPhotos.length })}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={async () => {
                        setUploadingExpenses(true);
                        try {
                          const token = localStorage.getItem('driver_token');
                          const response = await fetch(`${BACKEND_URL}/api/drivers/expense-reports`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              order_id: selectedOrderToComplete.id,
                              driver_expense: 0,  // Required field
                              ...expenseForm
                            })
                          });
                          if (response.ok) {
                            toast.success(t('driver_toast_expense_report_sent'));
                            // Update local state immediately
                            setSelectedOrderToComplete(prev => ({...prev, has_expense_report: true}));
                            setExpenseForm({
                              fuel_expense: 0,
                              toll_expense: 0,
                              parking_expense: 0,
                              taxi_expense: 0,
                              other_expense: 0,
                              other_expense_description: '',
                              receipts: []
                            });
                            setReceiptPhotos([]);
                            // Reload incomplete orders in background (don't wait)
                            loadIncompleteOrders().catch(console.error);
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || t('driver_toast_error_send_report'));
                            console.error('Error response:', err);
                            // If error is about existing report, reload data and update UI
                            if (err.detail && err.detail.includes('Ya existe un reporte')) {
                              setSelectedOrderToComplete(prev => ({...prev, has_expense_report: true}));
                              loadIncompleteOrders().catch(console.error);
                            }
                          }
                        } catch (error) {
                          console.error('Network error:', error);
                          toast.error(t('driver_toast_error_connection_expense_report'));
                        } finally {
                          setUploadingExpenses(false);
                        }
                      }}
                      disabled={uploadingExpenses}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      {uploadingExpenses ? t('driver_sending') : t('driver_send_expense_report')}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">{t('driver_complete_expense_done')}</p>
                )}
              </div>

              {/* Documentos adicionales (PDF / imágenes) — mismo almacén que adjuntos del servicio */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-800 mb-2">
                  <FileText className="w-5 h-5" />
                  {t('driver_completion_docs_title')}
                </h3>
                <p className="text-sm text-gray-600 mb-3">{t('driver_completion_docs_subtitle')}</p>
                <OrderServiceDocumentsField
                  files={completionServiceFiles}
                  onFilesChange={setCompletionServiceFiles}
                  disabled={
                    uploadingCompletionServiceDocs ||
                    selectedOrderToComplete.approval_status === 'approved' ||
                    !!selectedOrderToComplete.is_fully_completed
                  }
                  labels={{
                    title: t('driver_completion_docs_field_label'),
                    hint: t('driver_completion_docs_hint'),
                    browse: t('service_docs_browse'),
                    previewTitle: t('service_docs_preview'),
                    remove: t('service_docs_remove'),
                    maxFiles: t('service_docs_max_files'),
                    fileTooLarge: t('service_docs_file_too_large'),
                    invalidType: t('service_docs_invalid_type'),
                    fieldId: 'driver-complete-service',
                  }}
                  onValidationError={(msg) => toast.warning(msg)}
                />
                <Button
                  type="button"
                  className="mt-3 w-full bg-slate-700 hover:bg-slate-800"
                  disabled={
                    uploadingCompletionServiceDocs ||
                    completionServiceFiles.length === 0 ||
                    selectedOrderToComplete.approval_status === 'approved' ||
                    !!selectedOrderToComplete.is_fully_completed
                  }
                  onClick={async () => {
                    if (completionServiceFiles.length === 0) return;
                    setUploadingCompletionServiceDocs(true);
                    try {
                      const token = localStorage.getItem('driver_token');
                      const newDocs = await uploadDriverOrderServiceDocuments(
                        selectedOrderToComplete.id,
                        completionServiceFiles,
                        BACKEND_URL,
                        token
                      );
                      setSelectedOrderToComplete((prev) => ({
                        ...prev,
                        service_documents: [...(prev.service_documents || []), ...newDocs],
                      }));
                      setCompletionServiceFiles([]);
                      toast.success(t('driver_toast_completion_docs_uploaded'));
                      loadIncompleteOrders().catch(console.error);
                      loadData().catch(console.error);
                    } catch (err) {
                      console.error(err);
                      toast.error(err.message || t('driver_toast_completion_docs_error'));
                    } finally {
                      setUploadingCompletionServiceDocs(false);
                    }
                  }}
                >
                  {uploadingCompletionServiceDocs
                    ? t('driver_uploading_completion_docs')
                    : t('driver_upload_completion_docs')}
                </Button>
              </div>

              {/* Resumen de estado */}
              <div className="bg-gray-100 rounded-lg p-4">
                <h4 className="font-medium mb-2">{t('driver_complete_service_status_title')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_pickup_photos ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_pickup_photos ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_pickup')}
                  </div>
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_delivery_photos ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_delivery_photos ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_delivery')}
                  </div>
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_contract_photos ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_contract_photos ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_contract')}
                  </div>
                  <div className={`flex items-center gap-2 ${selectedOrderToComplete.has_expense_report ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedOrderToComplete.has_expense_report ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    {t('driver_complete_summary_expenses')}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCompleteServiceDialog(false);
                  setPickupPhotos([]);
                  setDeliveryPhotos([]);
                  setContractPhotos([]);
                  setReceiptPhotos([]);
                  setCompletionServiceFiles([]);
                  setExpenseForm({
                    fuel_expense: 0,
                    toll_expense: 0,
                    parking_expense: 0,
                    taxi_expense: 0,
                    other_expense: 0,
                    other_expense_description: '',
                    receipts: []
                  });
                }}
              >
                {t('driver_close')}
              </Button>
                {selectedOrderToComplete.has_pickup_photos && selectedOrderToComplete.has_delivery_photos && selectedOrderToComplete.has_expense_report && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    toast.success(t('driver_toast_service_completed'));
                    setShowCompleteServiceDialog(false);
                    setCompletionServiceFiles([]);
                    loadIncompleteOrders();
                  }}
                >
                  {t('driver_complete_finish')}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Assign to Fleet Member Dialog */}
      {showAssignDialog && selectedOrderForAssignment && (
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {selectedOrderForAssignment.driver_id === driver.id
                  ? t('driver_fleet_assign_service')
                  : t('driver_fleet_reassign_service')}
              </DialogTitle>
              <DialogDescription>
                {t('driver_fleet_assign_desc')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">{t('driver_fleet_service_block_title')}</h4>
                <p className="text-sm text-gray-700">
                  {selectedOrderForAssignment.car_brand} {selectedOrderForAssignment.car_model}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedOrderForAssignment.pickup_address} → {selectedOrderForAssignment.delivery_address}
                </p>
                {selectedOrderForAssignment.driver_name && (
                  <p className="text-sm text-blue-600 mt-2">
                    {t('driver_fleet_current_assignee')}{' '}
                    <strong>{selectedOrderForAssignment.driver_name}</strong>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{t('driver_select_driver')}</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {/* Fleet driver (himself) option */}
                  <div
                    className={`border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedOrderForAssignment.driver_id === driver.id ? 'border-blue-500 bg-blue-50' : ''
                    }`}
                    onClick={() => handleAssignToFleetMember(driver.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-green-700" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{driver.name} (Tú)</p>
                        <p className="text-xs text-gray-600">{driver.email}</p>
                      </div>
                      {selectedOrderForAssignment.driver_id === driver.id && (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                  
                  {/* Fleet members */}
                  {fleetMembers.filter(m => m.documents_validated).length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p className="text-sm">No hay choferes con documentos validados</p>
                      <p className="text-xs mt-1">Espera a que el admin apruebe los documentos o asígnalo a ti mismo</p>
                    </div>
                  ) : (
                    fleetMembers
                      .filter(m => m.documents_validated)
                      .map(member => (
                        <div
                          key={member.id}
                          className={`border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                            selectedOrderForAssignment.driver_id === member.id ? 'border-blue-500 bg-blue-50' : ''
                          }`}
                          onClick={() => handleAssignToFleetMember(member.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{member.name}</p>
                              <p className="text-xs text-gray-600">{member.email}</p>
                            </div>
                            {selectedOrderForAssignment.driver_id === member.id ? (
                              <CheckCircle className="w-5 h-5 text-blue-600" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-gray-300" />
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default DriverDashboard;
